/* eslint-disable max-lines, max-lines-per-function */
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '../../context/AuthContext';
import { AutoReconnectState } from '../../hooks/useAutoReconnect';
import {
  designCreditCopy,
  designCreditShortfall,
  useDesignCreditStatus,
} from '../../hooks/useDesignCreditStatus';
import {
  getPhaseBuildBusyLabel,
  getPhaseBuildLabel,
  getPhaseBuildTitle,
  isPhaseBuildActionEnabled,
  usePhaseBuildState,
} from '../../hooks/usePhaseBuildState';
import { isProjectUtilityAction, useProjectActionButtonHandlers } from '../../hooks/useProjectActionButtonHandlers';
import { ApiError, errorMessage } from '../../services/apiError';
import type {
  ChatMessage, DeveloperStatusStep, TriggerAgentsResponse, WebSearchActivity,
} from '../../services/chatService';
import { chatService } from '../../services/chatService';
import type { DesignPage } from '../../services/designService';
import { signalInsufficientCredits } from '../../services/creditGateEvents';
import { registerPlanningAction } from '../../services/planningActionRegistry';
import { ArtifactUpdatesAction } from '../ArtifactUpdatesAction';
import { projectService } from '../../services/projectService';
import { useAppSelector } from '../../store/hooks';
import { useGetDesignPagesQuery } from '../../store/services/designApi';
import { useGetPlanningStatusQuery } from '../../store/services/planningRuntimeApi';
import {
  supervisorButtonResumePhase,
  type SupervisorButton,
} from '../../types/supervisorButtons';
import { trackViewAgentResult, trackReviewClick, trackStartBuildingClick } from '../../utils/analytics';
import { isCreditError } from '../../utils/errorClassification';
import { getPhaseActionCreditShortfall, getTopUpCreditAmount } from '../../utils/creditGate';
import { normalizePhaseReadyTaskSummary } from '../../utils/phaseBuildPresentation';
import {
  planningStatusMatchesTrigger,
  reconcileAmbiguousPlanningTrigger,
} from '../../utils/planningTriggerReconciliation';
import {
  actionClassForSupervisorButton,
  iconClassForSupervisorButton,
  loadingLabelForSupervisorButton,
} from '../../utils/supervisorButtonIcons';
import { truncateWords } from '../../utils/textUtils';
import { ActionBarActions } from '../AiOverviewPanel/ActionBarActions';
import { QUOTA_QUEUE_PHASES } from '../AiOverviewPanel/ActionQuotaUpgradeBtn';
import { MessageIdentityLabel } from '../AiOverviewPanel/MessageIdentityLabel';
import { isCreditBlockedMessage } from '../AiOverviewPanel/creditRefillMessage';
import { PlanningStartCreditActions, usePlanningStartCredit } from '../AiOverviewPanel/PlanningStartCreditActions';
import RichMarkdown from '../AiOverviewPanel/RichMarkdown';
import {
  createActionInvocationId, isActionClicked, recordActionClick, recordUserActionClick, useActionBarClicksStore,
} from '../AiOverviewPanel/useActionBarClicks';
import { BillingModal, UpgradeModal } from '../BillingModal';
import { BrandPendulumMark } from '../BrandPendulumMark';
import { PhaseBuildStatus } from '../PhaseBuildStatus';
import { PlanningActionTasks } from '../PlanningActionTasks';
import { usePlanningActionSelection } from '../planningActionSelection';
import type { RecordUserActionOptions } from '../AiOverviewPanel/useActionBarClicks';
import { CreditRefillNotice } from '../CreditRefillNotice';
import { usePendingPlanningStart, usePlanningStart } from '../PlanningStartProvider';
import { QuotaResetLine } from '../QuotaResetLine';
import { useToast } from '../Toast/Toast';

import { MessageItemProps } from './ChatBoxTypes';
import {
  collectDeveloperPlanStates,
  DeveloperPlanState,
  parsePlanEnvelope,
  planEnvelopeVisibleMarkdown,
} from './developerPlan';
import { DevPlanButtons } from './DevPlanButtons';
import { GateUpgradeButton } from './GateUpgradeButton';
import { PlanningCreditRefusal } from './PlanningCreditRefusal';
import { throwIfPlanningCreditRefused, type PlanningCreditRefusalDetail } from './planningCreditRefusalError';
import { checkTierGate, dispatchStartBuild, useBuildResolvedListener } from './StartBuildingButton';
import { isRestorableSupervisorButton, suggestionMatchesContext } from './suggestionUtils';
import { useMediaBlobUrls } from './useMediaBlobUrls';
import { WebSearchActivityList } from './WebSearchActivity';


const WORD_LIMIT = 30;
const containsRichDiagram = (value: string): boolean => /```(?:mermaid|svg)\b/i.test(value);

const PlanningMessageIdentity: React.FC<{
  className?: string;
  kind?: 'ai' | 'user';
  name: string;
  isStreaming?: boolean;
  streamContent?: string;
}> = ({ className = '', kind = 'ai', name, isStreaming, streamContent }) => (
  <MessageIdentityLabel
    className={`planning-message__meta${className ? ` ${className}` : ''}`}
    kind={kind}
    name={name}
    isStreaming={isStreaming}
    streamContent={streamContent}
  />
);

const DEVELOPER_PROGRESS_STEPS = [
  { label: 'Hashing local files', threshold: 8, patterns: ['hash', 'local git files'] },
  { label: 'Checking diff', threshold: 34, patterns: ['diff', 'root hash', 'no code changes', 'code changes'] },
  { label: 'Syncing code', threshold: 78, patterns: ['upload', 'queue', 'queued', 'sync'] },
  { label: 'Indexing code graph', threshold: 92, patterns: ['index', 'graph', 'incremental', 'rebuild'] },
  { label: 'Inspecting code', threshold: 96, patterns: ['inspect', 'analyz', 'plan'] },
  { label: 'Responding', threshold: 100, patterns: ['response', 'answer', 'complete'] },
] as const;

const developerModeProgressLabel = (stepIndex: number, text: string): string => {
  const baseLabel = DEVELOPER_PROGRESS_STEPS[stepIndex]?.label ?? 'Developer Mode progress';
  if (baseLabel !== 'Indexing code graph') return baseLabel;

  const lowerText = text.toLowerCase();
  if (lowerText.includes('incremental') || lowerText.includes('updating the code graph')) {
    return 'Updating code graph';
  }
  if (lowerText.includes('full code graph') || lowerText.includes('rebuild')) {
    return 'Indexing full code graph';
  }
  return baseLabel;
};

const displayDeveloperStatusStepLabel = (
  step: DeveloperStatusStep | undefined,
  fallback: string,
): string => {
  const label = step?.label || fallback;
  const normalized = label.trim().toLowerCase();
  if (normalized === 'updating graph') return 'Updating code graph';
  if (normalized === 'indexing graph') return 'Indexing code graph';
  if (normalized === 'indexing full graph') return 'Indexing full code graph';
  return label;
};

const developerModeProgressStepIndex = (progress: number, text: string): number => {
  if (progress >= 100) return DEVELOPER_PROGRESS_STEPS.length - 1;

  const lowerText = text.toLowerCase();
  const matchedIndex = DEVELOPER_PROGRESS_STEPS.findIndex((step) => (
    step.patterns.some((pattern) => lowerText.includes(pattern))
  ));
  if (matchedIndex >= 0) return matchedIndex;

  let index = 0;
  for (let stepIndex = 0; stepIndex < DEVELOPER_PROGRESS_STEPS.length; stepIndex += 1) {
    if (progress >= DEVELOPER_PROGRESS_STEPS[stepIndex].threshold) {
      index = stepIndex;
    }
  }

  return index;
};

const isRunningStep = (step: DeveloperStatusStep): boolean => {
  const status = (step.status || '').toLowerCase();
  if (status) return status === 'running';
  return !step.ended_at && step.duration_ms == null && Boolean(step.started_at);
};

const TERMINAL_DEVELOPER_STATUSES = new Set([
  'complete',
  'completed',
  'failed',
  'idle',
  'cancelled',
  'canceled',
  'needs_plan_feedback',
  'stopped',
]);

const normalizeDeveloperStatus = (status?: string | null): string => {
  const normalized = (status || '').toLowerCase();
  return normalized === 'canceled' || normalized === 'stopped' ? 'cancelled' : normalized;
};

const isTerminalDeveloperStatus = (status?: string | null): boolean => (
  TERMINAL_DEVELOPER_STATUSES.has((status || '').toLowerCase())
);

const resolvedStatus = (
  step: DeveloperStatusStep | undefined,
  developerStatus?: string | null,
): string => {
  const stepStatus = normalizeDeveloperStatus(step?.status);
  if (stepStatus === 'cancelled' || stepStatus === 'failed') return stepStatus;
  const overallStatus = normalizeDeveloperStatus(developerStatus);
  if (isTerminalDeveloperStatus(overallStatus)) return overallStatus;
  return stepStatus;
};

const developerStatusStepsForDisplay = (
  steps?: readonly DeveloperStatusStep[],
  developerStatus?: string | null,
): DeveloperStatusStep[] => {
  const visibleSteps = (steps ?? []).filter((step) => step.label || step.message || step.detail);
  if (!isTerminalDeveloperStatus(developerStatus)) return visibleSteps;
  const status = normalizeDeveloperStatus(developerStatus);
  return visibleSteps.map((step) => (isRunningStep(step) ? { ...step, status } : step));
};

const durationMsFromStep = (step: DeveloperStatusStep, nowMs = Date.now()): number | null => {
  if (typeof step.duration_ms === 'number' && Number.isFinite(step.duration_ms)) {
    return Math.max(0, step.duration_ms);
  }
  if (step.started_at && step.ended_at) {
    const startedAtMs = Date.parse(step.started_at);
    const endedAtMs = Date.parse(step.ended_at);
    if (Number.isFinite(startedAtMs) && Number.isFinite(endedAtMs)) {
      return Math.max(0, endedAtMs - startedAtMs);
    }
  }
  if (!isRunningStep(step) || !step.started_at) return null;
  const startedAtMs = Date.parse(step.started_at);
  return Number.isFinite(startedAtMs) ? Math.max(0, nowMs - startedAtMs) : null;
};

const totalDurationMsFromSteps = (steps: readonly DeveloperStatusStep[], nowMs = Date.now()): number | null => {
  const total = steps.reduce((sum, step) => {
    const durationMs = durationMsFromStep(step, nowMs);
    return durationMs === null ? sum : sum + durationMs;
  }, 0);
  return total > 0 ? total : null;
};

const formatDurationMs = (durationMs: number, running = false): string => {
  if (running && durationMs < 1000) return '0s';
  if (durationMs < 1000) return `${Math.max(0, Math.round(durationMs))}ms`;
  const seconds = durationMs / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  return `${minutes}m ${remainingSeconds}s`;
};

const formatStepDuration = (step: DeveloperStatusStep, nowMs = Date.now()): string => {
  const running = isRunningStep(step);
  const durationMs = durationMsFromStep(step, nowMs);
  if (durationMs === null) return running ? '0s' : 'Done';
  return formatDurationMs(durationMs, running);
};

const stepStatusClass = (step: DeveloperStatusStep): string => {
  if (isRunningStep(step)) return 'running';
  return (step.status || 'completed').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
};

const stepStateLabel = (step: DeveloperStatusStep): string => {
  if (isRunningStep(step)) return 'Running';
  const status = (step.status || '').toLowerCase();
  if (status === 'failed') return 'Failed';
  if (status === 'cancelled' || status === 'canceled' || status === 'stopped') return 'Stopped';
  if (status === 'needs_plan_feedback') return 'Waiting';
  return 'Done';
};

const resolvedProgressLabel = (
  step: DeveloperStatusStep | undefined,
  fallback: string,
  developerStatus?: string | null,
  totalDurationMs?: number | null,
): string => {
  const status = resolvedStatus(step, developerStatus);
  if (!status || status === 'running') return fallback;
  if (status === 'failed') return 'Sync failed';
  if (status === 'cancelled') return 'Sync stopped';
  if (status === 'needs_plan_feedback') return 'Waiting for feedback';
  if (
    (status === 'complete' || status === 'completed' || status === 'idle')
    && totalDurationMs !== null
    && totalDurationMs !== undefined
  ) {
    return `sync completed in ${formatDurationMs(totalDurationMs)}`;
  }
  if ((status === 'complete' || status === 'completed' || status === 'idle') && step) {
    const duration = formatStepDuration(step);
    return duration === 'Done' ? 'sync completed' : `sync completed in ${duration}`;
  }
  if (status === 'complete' || status === 'completed' || status === 'idle') return 'sync completed';
  return fallback;
};

const resolvedProgressValue = (
  step: DeveloperStatusStep | undefined,
  fallback: string,
  developerStatus?: string | null,
): string => {
  const status = resolvedStatus(step, developerStatus);
  if (!status || status === 'running') return fallback;
  if (status === 'failed') return 'Failed';
  if (status === 'cancelled') return 'Stopped';
  if (status === 'needs_plan_feedback') return 'Waiting';
  return fallback;
};

const resolvedProgressSummaryText = (
  step: DeveloperStatusStep | undefined,
  fallback: string,
  developerStatus?: string | null,
): string => {
  const status = resolvedStatus(step, developerStatus);
  if (!status || status === 'running') return fallback;
  if (status === 'failed') return 'Developer Mode sync failed.';
  if (status === 'cancelled') return 'Developer Mode sync stopped.';
  if (status === 'needs_plan_feedback') return 'Developer Mode is waiting for feedback.';
  if (status === 'complete' || status === 'completed' || status === 'idle') {
    return 'Developer Mode sync completed.';
  }
  return fallback;
};

const currentDeveloperStatusStep = (
  steps?: readonly DeveloperStatusStep[],
  developerStatus?: string | null,
): DeveloperStatusStep | undefined => {
  const visibleSteps = developerStatusStepsForDisplay(steps, developerStatus);
  if (isTerminalDeveloperStatus(developerStatus)) return visibleSteps[visibleSteps.length - 1];
  return [...visibleSteps].reverse().find(isRunningStep) || visibleSteps[visibleSteps.length - 1];
};

const useRunningStepClock = (enabled: boolean): number => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [enabled]);

  return nowMs;
};

const stepFallbackText = (step: DeveloperStatusStep): string => (
  step.detail || step.message || step.label || 'Developer Mode updated this step.'
);

const SyncProgress: React.FC<{
  progress: number;
  text: string;
  steps?: DeveloperStatusStep[];
  projectId?: string | null;
  sessionId?: string | null;
  jobId?: string | null;
  developerStatus?: string | null;
}> = ({
  progress, text, steps = [], projectId, sessionId, jobId, developerStatus,
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const visibleSteps = useMemo(
    () => developerStatusStepsForDisplay(steps, developerStatus),
    [developerStatus, steps],
  );
  const fallbackRoundedProgress = Math.max(0, Math.min(100, Math.round(progress)));
  const hasRunningStep = visibleSteps.some(isRunningStep);
  const nowMs = useRunningStepClock(hasRunningStep);
  const currentStep = isTerminalDeveloperStatus(developerStatus)
    ? visibleSteps[visibleSteps.length - 1]
    : [...visibleSteps].reverse().find(isRunningStep) || visibleSteps[visibleSteps.length - 1];
  const currentStepRunning = currentStep
    ? !isTerminalDeveloperStatus(developerStatus) && isRunningStep(currentStep)
    : fallbackRoundedProgress < 100 && !isTerminalDeveloperStatus(developerStatus);
  const stepProgress = currentStepRunning
    && typeof currentStep?.progress === 'number'
    && Number.isFinite(currentStep.progress)
    ? currentStep.progress
    : undefined;
  const roundedProgress = Math.max(0, Math.min(100, Math.round(stepProgress ?? progress)));
  const width = Math.max(3, roundedProgress);
  const stepIndex = developerModeProgressStepIndex(roundedProgress, text);
  const terminalStatus = resolvedStatus(currentStep, developerStatus);
  const isCompletedStatus = terminalStatus === 'complete' || terminalStatus === 'completed' || terminalStatus === 'idle';
  const rawLabel = displayDeveloperStatusStepLabel(
    currentStep,
    developerModeProgressLabel(stepIndex, text),
  );
  const totalDurationMs = totalDurationMsFromSteps(visibleSteps, nowMs);
  const label = currentStepRunning
    ? rawLabel
    : resolvedProgressLabel(currentStep, rawLabel, developerStatus, totalDurationMs);
  const progressValue = resolvedProgressValue(currentStep, `${roundedProgress}%`, developerStatus);
  const canStop = Boolean(projectId && sessionId && currentStepRunning);

  const handleStopSync = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!projectId || !sessionId || isStopping) return;
    setIsStopping(true);
    try {
      await projectService.cancelDeveloperModeProcessing(projectId, {
        session_id: sessionId,
        job_id: jobId || currentStep?.job_id || null,
      });
    } catch (err) {
      console.error('Failed to stop Developer Mode sync', err);
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <div
      className={`sync-progress${currentStepRunning ? ' sync-progress--active' : ''}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={roundedProgress}
      aria-label={label}
    >
      <div className="sync-progress__head">
        <span className="sync-progress__label">{label}</span>
        <span className="sync-progress__stats">
          {currentStep && !isCompletedStatus && (
            <span className={`sync-progress__duration${isRunningStep(currentStep) ? ' is-live' : ''}`}>
              {formatStepDuration(currentStep, nowMs)}
            </span>
          )}
          <span className="sync-progress__value">{progressValue}</span>
          {canStop && (
            <button
              className="sync-progress__stop app-action-btn app-action-btn--danger app-action-btn--compact"
              aria-busy={isStopping}
              disabled={isStopping}
              onClick={handleStopSync}
              title="Stop workspace sync"
              type="button"
            >
              {isStopping ? 'Stopping' : 'Stop sync'}
            </button>
          )}
        </span>
      </div>
      <div className="sync-progress__track">
        <div className="sync-progress__bar" style={{ width: `${width}%` }} />
      </div>
      {visibleSteps.length > 0 && (
        <details
          className="sync-progress__history"
          open={historyOpen}
          onToggle={(event) => setHistoryOpen(event.currentTarget.open)}
        >
          <summary className="sync-progress__history-summary">
            <span className="sync-progress__history-title">Step history</span>
            <span className="sync-progress__history-count">
              {visibleSteps.length} {visibleSteps.length === 1 ? 'step' : 'steps'}
            </span>
          </summary>
          <div className="sync-progress__steps">
            {visibleSteps.map((step, index) => (
              <details
                className={`sync-progress-step sync-progress-step--${stepStatusClass(step)}`}
                key={step.id || `${step.step_key}-${step.started_at || index}`}
              >
                <summary className="sync-progress-step__summary">
                  <span className="sync-progress-step__index" aria-hidden="true">{index + 1}</span>
                  <span className="sync-progress-step__body">
                    <span className="sync-progress-step__label">
                      {displayDeveloperStatusStepLabel(step, `Step ${index + 1}`)}
                    </span>
                    <span className="sync-progress-step__state">{stepStateLabel(step)}</span>
                  </span>
                  <span className={`sync-progress-step__duration${isRunningStep(step) ? ' is-live' : ''}`}>
                    {formatStepDuration(step, nowMs)}
                  </span>
                </summary>
                <dl className="sync-progress-step__meta">
                  <div>
                    <dt>Why</dt>
                    <dd>{step.reason || 'This step was required to prepare the Developer Mode response.'}</dd>
                  </div>
                  <div>
                    <dt>What</dt>
                    <dd>{stepFallbackText(step)}</dd>
                  </div>
                  <div>
                    <dt>Duration</dt>
                    <dd className={isRunningStep(step) ? 'is-live' : undefined}>
                      {formatStepDuration(step, nowMs)}
                    </dd>
                  </div>
                </dl>
              </details>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// Get display name for message author
const getDisplayName = (message: ChatMessage, userDisplayName: string): string => {
  if (message.author === 'user') return message.sender_name || message.sender_email || userDisplayName;
  if (message.author === 'system') return 'System';

  return 'Tech Lead Agent';
};

// Markdown content renderer
const MarkdownContent = React.memo<{ content: string }>(({ content }) => (
  <div className="planning-message__content">
    <RichMarkdown content={normalizePhaseReadyTaskSummary(content)} />
  </div>
));

MarkdownContent.displayName = 'MarkdownContent';

const COLLAPSE_HEIGHT = '4.5em';
const COLLAPSE_MASK = 'linear-gradient(to bottom, black 50%, transparent 100%)';

// Set inline styles for collapsed state
const applyCollapsedStyles = (el: HTMLDivElement) => {
  el.style.maxHeight = COLLAPSE_HEIGHT;
  el.style.overflowY = 'hidden';
  el.style.webkitMaskImage = COLLAPSE_MASK;
  el.style.maskImage = COLLAPSE_MASK;
};

// Clear all inline expand/collapse styles
const clearStyles = (el: HTMLDivElement) => {
  el.style.maxHeight = '';
  el.style.overflowY = '';
  el.style.webkitMaskImage = '';
  el.style.maskImage = '';
};

// Animate expanding: 4.5em (inline from prior collapse) → scrollHeight
const runExpand = (el: HTMLDivElement): (() => void) => {
  el.style.webkitMaskImage = '';
  el.style.maskImage = '';
  el.style.maxHeight = `${el.scrollHeight}px`;

  const done = () => { clearStyles(el); el.removeEventListener('transitionend', done); };

  el.addEventListener('transitionend', done);

  // Safety: if transitionend never fires (e.g. same value, element unmounted),
  // clear stale inline max-height so the container can grow freely.
  const timer = setTimeout(done, 600);

  return () => { clearTimeout(timer); el.removeEventListener('transitionend', done); };
};

// Animate collapsing: natural height → 4.5em
const runCollapse = (el: HTMLDivElement) => {
  el.style.maxHeight = `${el.scrollHeight}px`;
  el.style.overflowY = 'hidden';
  void el.offsetHeight;
  applyCollapsedStyles(el);
};

// Wrapper that animates expand/collapse using inline styles only (no CSS class toggling)
const ContentWrap: React.FC<{ collapsed: boolean; children: React.ReactNode }> = ({ collapsed, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isFirst = useRef(true);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    // First render: set initial state without animation
    if (isFirst.current) {
      isFirst.current = false;
      if (collapsed) applyCollapsedStyles(el);

      return;
    }

    return collapsed ? runCollapse(el) : runExpand(el);
  }, [collapsed]);

  return (
    <div ref={ref} className="msg-content-wrap">
      {children}
    </div>
  );
};

// Toggle arrow button — SVG chevron that inherits color
// Uses a key counter to force remount on click, replaying the CSS roll-up animation.
const ExpandToggle: React.FC<{ isExpanded: boolean; onClick: () => void }> = ({ isExpanded, onClick }) => {
  const [animKey, setAnimKey] = useState(0);
  const handleClick = () => { setAnimKey((k) => k + 1); onClick(); };

  return (
    <button key={animKey} className="read-more-btn" onClick={handleClick} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
      <svg width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={isExpanded ? { transform: 'rotate(180deg)' } : undefined}>
        <polyline points="1 1 7 7 13 1" />
      </svg>
    </button>
  );
};

// Expandable markdown content with arrow toggle (for streaming)
interface ExpandableContentProps {
  content: string;
  isStreaming?: boolean;
  isLast?: boolean;
}

const ExpandableContent: React.FC<ExpandableContentProps> = ({ content, isStreaming, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(isLast));
  const hasDiagram = containsRichDiagram(content);
  const { isTruncated } = truncateWords(content, WORD_LIMIT);

  // Auto-expand when becoming last, auto-collapse when no longer last
  useEffect(() => {
    setIsExpanded(Boolean(isLast));
  }, [isLast]);

  const collapsed = isTruncated && !hasDiagram && !isStreaming && !isExpanded;

  return (
    <>
      <ContentWrap collapsed={collapsed}>
        <MarkdownContent content={content} />
      </ContentWrap>
      {isTruncated && !hasDiagram && !isStreaming && !(isLast && isExpanded) && (
        <ExpandToggle isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
      )}
    </>
  );
};

// Lightbox overlay for full-size image viewing
interface ChatMediaLightboxProps {
  url: string;
  type: string;
  onClose: () => void;
}

const ChatMediaLightbox: React.FC<ChatMediaLightboxProps> = ({ url, type, onClose }) => {
  const isDoc = ['pdf', 'txt', 'md'].includes(type);

  return (
    <div className="chat-media-lightbox" onClick={onClose}>
      {isDoc ? (
        <div className="chat-media-lightbox__doc-container" onClick={(e) => e.stopPropagation()}>
          <iframe
            src={url}
            title="Document Preview"
            sandbox="allow-scripts allow-same-origin"
            className="chat-media-lightbox__iframe"
          />
        </div>
      ) : (
        <img src={url} alt="Full size" onClick={(e) => e.stopPropagation()} />
      )}
      <button className="chat-media-lightbox__close" onClick={onClose} type="button">
        &times;
      </button>
    </div>
  );
};

// Map system message keywords to right-panel tab names
const SYSTEM_MSG_TAB_MAP: { pattern: RegExp; tab: string; subTab?: string; extractPage?: boolean }[] = [
  { pattern: /System Requirements/i, tab: 'requirements' },
  { pattern: /User Flow/i, tab: 'user-flow' },
  { pattern: /Design Agent/i, tab: 'design', subTab: 'pages', extractPage: true },
  { pattern: /System Architect/i, tab: 'architecture' },
  { pattern: /Project Manager/i, tab: 'tasks' },
];

const PLANNING_START_PATTERN = /^(Planning your project|Continue planning)\.\.\./i;
const WARMUP_START_PATTERN = /^Warming Up\.\.\.$/i;
const WARMUP_COMPLETED_PATTERN = /^Warmup completed\.$/i;

const isPlanningStartMessage = (content: string): boolean => PLANNING_START_PATTERN.test(content.trim());
const isWarmupStartMessage = (content: string): boolean => WARMUP_START_PATTERN.test(content.trim());
const isWarmupTerminalMessage = (content: string): boolean => {
  const normalized = content.trim();

  return WARMUP_COMPLETED_PATTERN.test(normalized)
    || /cancel(?:led|ed|lation)/i.test(normalized)
    || /insufficient credits/i.test(normalized);
};

const isSameWarmupSession = (current: ChatMessage, next: ChatMessage): boolean => (
  !current.session_id || !next.session_id || current.session_id === next.session_id
);
const isWarmupLifecycleMessage = (content: string): boolean => (
  isPlanningStartMessage(content) || isWarmupStartMessage(content) || isWarmupTerminalMessage(content)
);
const findNextWarmupLifecycleMessage = (messages: ChatMessage[], index: number): ChatMessage | undefined => (
  messages.slice(index + 1).find((next) => (
    isSameWarmupSession(messages[index], next) && isWarmupLifecycleMessage(next.content)
  ))
);

const shouldShowWarmupSpinner = (
  messages: ChatMessage[],
  index: number,
  isLastGroup: boolean,
): boolean => (
  isLastGroup
  && isWarmupStartMessage(messages[index].content)
  && !findNextWarmupLifecycleMessage(messages, index)
);

const shouldShowAgentWarmupSpinner = (messages: ChatMessage[], index: number): boolean => {
  const message = messages[index];

  return message.author !== 'user'
    && isWarmupStartMessage(message.content)
    && !findNextWarmupLifecycleMessage(messages, index);
};

const asTechLeadLifecycleMessage = (message: ChatMessage): ChatMessage => ({
  ...message,
  author: 'assistant',
});

// Inline bold markdown: replaces **text** with <strong>text</strong>
const renderInlineBold = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  ));
};

const SystemMessageText: React.FC<{
  content: string;
  showSpinner?: boolean;
}> = ({ content, showSpinner = false }) => (
  <span className={`system-msg-text${showSpinner ? ' system-msg-text--with-spinner' : ''}`}>
    {showSpinner && <BrandPendulumMark animated={false} className="bubble__typing-mark system-msg-warmup-mark" />}
    <span className="system-msg-copy">
      {renderInlineBold(normalizePhaseReadyTaskSummary(content))}
    </span>
  </span>
);

type DesignReviewPhase = 'user_flow' | 'first_page' | 'all_pages' | 'insufficient_credits';
type ActionGatePhase = DesignReviewPhase | 'review_build' | 'auto' | 'artifact_updates' | QuotaQueuePhase;
/**
 * Work waiting for its plan's hourly quota. Nothing restarts by itself: planning waits for Continue, a held build for
 * Resume build, and held artifact updates (the notice phase) for Run Updates on their own next offer.
 */
type QuotaQueuePhase = 'quota_queue' | 'quota_queue_build' | 'quota_queue_notice';
type ActionMetadata = Pick<ChatMessage, 'phase' | 'allowed_buttons' | 'buttons' | 'clicked_button' | 'clicked_buttons'>
  & Partial<Pick<ChatMessage, 'created_at'>>;

const getActionGatePhase = (
  message?: Pick<ChatMessage, 'phase' | 'allowed_buttons' | 'buttons'> | null,
): ActionGatePhase | null => {
  if (!message || (!Array.isArray(message.buttons) && !Array.isArray(message.allowed_buttons))) return null;
  const phase = message.phase;
  if (
    phase === 'user_flow'
    || phase === 'first_page'
    || phase === 'all_pages'
    || phase === 'insufficient_credits'
    || phase === 'review_build'
    || phase === 'auto'
    || phase === 'artifact_updates'
    || phase === 'quota_queue'
    || phase === 'quota_queue_build'
    || phase === 'quota_queue_notice'
  ) return phase;
  return null;
};

const getAllowedActionButtons = (message?: Pick<ChatMessage, 'allowed_buttons'> | null): string[] => {
  const allowedButtons = message?.allowed_buttons;

  return Array.isArray(allowedButtons)
    ? allowedButtons.filter((button): button is string => typeof button === 'string' && button.length > 0)
    : [];
};

const getStructuredActionButtons = (
  message?: Pick<ChatMessage, 'buttons' | 'allowed_buttons'> | null,
): SupervisorButton[] => {
  const buttons = message?.buttons;

  if (Array.isArray(buttons)) {
    return buttons.filter((button): button is SupervisorButton => (
      Boolean(button) && typeof button.key === 'string' && button.key.length > 0
    ));
  }

  return getAllowedActionButtons(message).map((key) => ({
    key,
    kind: 'workflow',
    label: key,
    action: key === 'continue' || key === 'credit_continue' ? 'resume_design' : 'prefill_chat',
  }));
};

const getClickedActionButtons = (
  message?: Pick<ChatMessage, 'clicked_button' | 'clicked_buttons'> | null,
): string[] => {
  const clickedButtons = message?.clicked_buttons;
  const buttons = Array.isArray(clickedButtons)
    ? clickedButtons.filter((button): button is string => typeof button === 'string' && button.length > 0)
    : [];
  if (
    typeof message?.clicked_button === 'string'
    && message.clicked_button
    && !buttons.includes(message.clicked_button)
  ) {
    buttons.push(message.clicked_button);
  }
  return buttons;
};

const getDesignReviewPhase = (
  message?: Pick<ChatMessage, 'phase' | 'allowed_buttons' | 'buttons'> | null,
): DesignReviewPhase | null => {
  const phase = getActionGatePhase(message);
  if (
    phase === 'user_flow'
    || phase === 'first_page'
    || phase === 'all_pages'
    || phase === 'insufficient_credits'
  ) return phase;
  return null;
};

type SystemMessageTone = 'green' | 'orange' | null;

const systemMessageTone = (
  content: string,
  actionPhase: ActionGatePhase | null = null,
): SystemMessageTone => {
  const normalized = content.trim();

  if (
    /^Continue planning/i.test(normalized)
    || actionPhase === 'user_flow'
    || actionPhase === 'first_page'
    || actionPhase === 'all_pages'
    || actionPhase === 'insufficient_credits'
  ) return 'green';
  if (
    /^Planning your project/i.test(normalized)
    || isWarmupStartMessage(normalized)
    || isWarmupTerminalMessage(normalized)
    || actionPhase === 'review_build'
    || actionPhase === 'quota_queue'
    || actionPhase === 'quota_queue_build'
    || actionPhase === 'quota_queue_notice'
  ) return 'orange';

  return null;
};

const systemMessageToneClass = (messages: Array<string | ChatMessage>): string => {
  const tones = messages.map((message) => (
    typeof message === 'string'
      ? systemMessageTone(message)
      : systemMessageTone(message.content, getActionGatePhase(message))
  ));

  if (tones.includes('green')) return ' planning-message--artifact-green';
  if (tones.includes('orange')) return ' planning-message--artifact-orange';
  return '';
};

const ChatActionBar: React.FC<React.PropsWithChildren<{
  entryId?: string;
  current: boolean;
  label?: string;
}>> = ({ children, entryId, current, label = 'Planning actions' }) => (
  <div className="ai-overview-action-bar system-msg-review-actions"
    data-action-source-entry-id={entryId} data-action-mirror-owner={current ? 'current' : 'historical'}
    data-action-owner-label={label}>
    <ActionBarActions attentionActive={current}>{children}</ActionBarActions>
  </div>
);

// Extracted Review + Build button pair (reused in merged and standalone contexts)
const ReviewBtn: React.FC<{
  phase?: ActionGatePhase | null;
  onClickStart?: (displayedLabel: string) => void;
}> = ({ phase, onClickStart }) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loading) return;
    const done = () => setLoading(false);

    window.addEventListener('review_wizard_ready', done);

    return () => window.removeEventListener('review_wizard_ready', done);
  }, [loading]);

  return (
    <button
      className="ai-overview-action-btn system-msg-review-btn system-msg-review-btn--review"
      disabled={loading}
      aria-busy={loading || undefined}
      onClick={() => {
        trackReviewClick();
        onClickStart?.('Review');
        setLoading(true);
        window.dispatchEvent(new globalThis.CustomEvent('trigger_review_mode', { detail: { phase } }));
      }}
      type="button"
    >{loading ? <><span className="review-btn-spinner" /> Reviewing...</> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg> Review</>}</button>
  );
};

export const ContinueBtn: React.FC<{
  phase?: ActionGatePhase;
  button?: SupervisorButton;
  entryId?: string;
  projectId?: string | null;
  sessionId?: string | null;
  current?: boolean;
  onClickStart?: (
    displayedLabel: string,
    options?: RecordUserActionOptions,
  ) => string | undefined | Promise<string | undefined>;
}> = ({
  phase,
  button,
  entryId,
  projectId,
  sessionId,
  current = true,
  onClickStart,
}) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const pendingRef = useRef(false);

  useEffect(() => {
    const reset = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<{
        ok?: boolean;
        entryId?: string | null;
      }>).detail;

      // Ignore resolutions scoped to a different gate entry.
      if (detail?.entryId && detail.entryId !== entryId) return;
      // Legacy unscoped resolutions only belong to this button while its own
      // request is pending. Otherwise another Continue could mark it done.
      if (!pendingRef.current) return;
      pendingRef.current = false;
      setLoading(false);
      if (detail?.ok === false) return;
      void recordActionClick(
        entryId,
        phase || '',
        button?.key || 'continue',
        projectId,
        sessionId,
      );
      setDone(true);
    };

    window.addEventListener('design_resume_resolved', reset);

    return () => window.removeEventListener('design_resume_resolved', reset);
  }, [button?.key, entryId, phase, projectId, sessionId]);

  const taskSelection = usePlanningActionSelection({
    projectId, entryId, phase, buttons: button ? [button] : undefined, current,
  });
  const baseLabel = button?.label || 'Continue';
  const label = taskSelection.labelFor(baseLabel);
  const enabled = !loading && !done && button?.enabled !== false && !taskSelection.disabled;
  const handleClick = useCallback(async () => {
    if (!enabled || pendingRef.current) return;
    pendingRef.current = true;
    setLoading(true);
    let planningActionInvocationId = taskSelection.preparedInvocationId;
    try {
      if (!planningActionInvocationId) {
        const options = taskSelection.selection ? {
          required: true, planningSelection: taskSelection.selection, baseLabel,
          onRecorded: (id: string) => {
            planningActionInvocationId = id;
            taskSelection.markPrepared(id, label);
          },
        } : undefined;

        const recordedInvocationId = onClickStart
          ? await onClickStart(label, options)
          : await recordUserActionClick(
            entryId, phase || '', button?.key || 'continue', label, projectId, sessionId, options,
          );

        planningActionInvocationId = recordedInvocationId ?? planningActionInvocationId;
      }
      if (!planningActionInvocationId) throw new Error('Missing planning action invocation.');
    } catch {
      pendingRef.current = false;
      setLoading(false);
      return;
    }
    const resumePhase = phase === 'insufficient_credits' ? 'auto' : phase;

    window.dispatchEvent(new globalThis.CustomEvent('resume_design_continue', {
      detail: {
        phase: resumePhase,
        artifactType: resumePhase === 'user_flow' ? 'user-flow' : 'design',
        entryId,
        ...(planningActionInvocationId ? { planningActionInvocationId } : {}),
      },
    }));
  }, [
    baseLabel, button?.key, enabled, entryId, label, onClickStart, phase, projectId, sessionId,
    taskSelection.selection, taskSelection.preparedInvocationId, taskSelection.markPrepared,
  ]);

  useEffect(() => {
    if (!projectId || !entryId) return undefined;

    return registerPlanningAction({
      projectId,
      sessionId: sessionId ?? null,
      entryId: taskSelection.actionIdentity ?? entryId,
      buttonKey: button?.key || 'continue',
      label,
      enabled,
      current,
      invoke: handleClick,
    });
  }, [button?.key, current, enabled, entryId, handleClick, label, projectId, sessionId, taskSelection.actionIdentity]);

  if (done) return <ContinuedBtn button={button} />;

  return (
    <button
      className="ai-overview-action-btn ai-overview-action-btn--continue system-msg-review-btn system-msg-review-btn--continue"
      disabled={!enabled}
      aria-busy={loading || undefined}
      onClick={handleClick}
      type="button"
    >{loading
        ? <><BrandPendulumMark animated={false} className="review-btn-spinner" /> Continuing...</>
        : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><polygon points="5 3 19 12 5 21 5 3" /></svg> {label}</>}</button>
  );
};

export const BuildBtn: React.FC<{
  projectId?: string | null;
  onInvocation?: (displayedLabel: string) => void;
  onBuildStart?: () => void;
  showStatus?: boolean;
}> = ({ projectId, onInvocation, onBuildStart, showStatus = true }) => {
  const [loading, setLoading] = useState(false);
  const clickIdRef = useRef<string | null>(null);
  const phaseBuild = usePhaseBuildState(projectId);
  const phase = phaseBuild.state?.current_phase;
  const creditShortfall = getPhaseActionCreditShortfall(phaseBuild.state);
  const buildEnabled = isPhaseBuildActionEnabled(phaseBuild.state);

  useBuildResolvedListener(clickIdRef, setLoading);

  return (
    <div className="system-msg-phase-build">
      {showStatus && <PhaseBuildStatus source="planning:message-phase-build" state={phaseBuild.state} compact />}
      <button
        className="ai-overview-action-btn ai-overview-action-btn--build system-msg-review-btn system-msg-review-btn--build"
        aria-busy={loading || undefined}
        disabled={
          loading
          || phaseBuild.isLoading
          || (!buildEnabled && creditShortfall <= 0)
        }
        title={getPhaseBuildTitle(phaseBuild.state)}
        onClick={() => {
          onInvocation?.(getPhaseBuildLabel(phaseBuild.state, phaseBuild.isLoading));
          if (creditShortfall > 0) {
            signalInsufficientCredits(creditShortfall);

            return;
          }
          trackStartBuildingClick('chat_message');
          onBuildStart?.();
          setLoading(true);
          dispatchStartBuild(clickIdRef, phase?.id);
        }}
        type="button"
      >{loading
          ? <><span className="review-btn-spinner" /> {getPhaseBuildBusyLabel(phaseBuild.state)}</>
          : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg> {getPhaseBuildLabel(phaseBuild.state, phaseBuild.isLoading)}</>}
      </button>
    </div>
  );
};

export const CreditButtons: React.FC<{
  projectId?: string | null;
  sessionId?: string | null;
  entryId?: string;
  phase?: string;
  buttons?: SupervisorButton[];
  isActive?: boolean;
  wasContinued?: boolean;
}> = ({
  projectId, sessionId, entryId, phase = '', buttons = [], isActive = false, wasContinued = false,
}) => {
  const [activeBillingView, setActiveBillingView] = useState<'credits' | 'plans' | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const pendingRef = useRef(false);
  const phaseBuild = usePhaseBuildState(projectId);
  const designCredit = useDesignCreditStatus(projectId);
  const designCreditLine = designCreditCopy(designCredit);

  useEffect(() => {
    const resolved = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<{ ok?: boolean; entryId?: string | null }>).detail;

      // Resolutions scoped to another gate entry are not ours; plain events
      // (legacy dispatchers without entryId) only apply while we are pending.
      if (detail?.entryId && detail.entryId !== entryId) return;
      if (!pendingRef.current) return;
      pendingRef.current = false;
      setLoading(false);
      // ok:false — resume failed or was credit-blocked; stay actionable.
      if (detail?.ok === false) return;
      void recordActionClick(entryId, phase, 'credit_continue', projectId, sessionId);
      setDone(true);
    };

    window.addEventListener('design_resume_resolved', resolved);

    return () => window.removeEventListener('design_resume_resolved', resolved);
  }, [entryId, phase, projectId, sessionId]);

  const continueButton = buttons.find((button) => button.key === 'credit_continue');
  const taskSelection = usePlanningActionSelection({
    projectId, entryId, phase, buttons, current: isActive,
  });
  const baseLabel = continueButton?.label || 'Continue';
  const continueLabel = taskSelection.labelFor(baseLabel);
  const continueEnabled = !loading
    && !wasContinued
    && !done
    && continueButton?.enabled !== false && !taskSelection.disabled;
  const handleContinue = useCallback(async () => {
    if (!continueEnabled || pendingRef.current) return;
    pendingRef.current = true;
    setLoading(true);
    let planningActionInvocationId = taskSelection.preparedInvocationId;
    try {
      if (!planningActionInvocationId) {
        const recordedInvocationId = await recordUserActionClick(
          entryId, phase, 'credit_continue', continueLabel, projectId, sessionId,
          taskSelection.selection ? {
            required: true, planningSelection: taskSelection.selection, baseLabel,
            onRecorded: (id) => {
              planningActionInvocationId = id;
              taskSelection.markPrepared(id, continueLabel);
            },
          } : undefined,
        );

        planningActionInvocationId = recordedInvocationId ?? planningActionInvocationId;
      }
      if (!planningActionInvocationId) throw new Error('Missing planning action invocation.');
    } catch {
      pendingRef.current = false;
      setLoading(false);
      return;
    }
    // Generalized Continue — backend picks up from wherever the credit-blocked
    // run left off (typically parallel resume of remaining pages).
    window.dispatchEvent(new globalThis.CustomEvent('resume_design_continue', {
      detail: {
        phase: supervisorButtonResumePhase(continueButton),
        artifactType: 'design',
        entryId,
        ...(planningActionInvocationId ? { planningActionInvocationId } : {}),
      },
    }));
  }, [
    baseLabel, continueButton, continueEnabled, continueLabel, entryId, phase, projectId, sessionId,
    taskSelection.selection, taskSelection.preparedInvocationId, taskSelection.markPrepared,
  ]);

  useEffect(() => {
    if (!projectId || !entryId) return undefined;

    return registerPlanningAction({
      projectId,
      sessionId: sessionId ?? null,
      entryId: taskSelection.actionIdentity ?? entryId,
      buttonKey: 'credit_continue',
      label: continueLabel,
      enabled: continueEnabled,
      current: isActive,
      invoke: handleContinue,
    });
  }, [
    continueEnabled, continueLabel, entryId, handleContinue, isActive, projectId, sessionId,
    taskSelection.actionIdentity,
  ]);

  if (wasContinued || done) {
    return (
      <ChatActionBar entryId={entryId} current={false}>
        <ReviewBtn
          phase={phase as ActionGatePhase}
          onClickStart={(label) => {
            void recordUserActionClick(
              entryId, phase, 'review', label, projectId, sessionId,
            );
            void recordActionClick(entryId, phase, 'review', projectId, sessionId);
          }}
        />
        <button
          className="ai-overview-action-btn ai-overview-action-btn--selected system-msg-review-btn system-msg-review-btn--continue system-msg-review-btn--selected"
          disabled
          type="button"
        >{continueButton?.doneLabel || continueButton?.done_label || '✓ Continued'}</button>
      </ChatActionBar>
    );
  }

  return (
    <>
      {designCreditLine && (
        <span className="system-msg-credit-copy">{designCreditLine}</span>
      )}
      {isActive && !loading && <CreditRefillNotice projectId={projectId} className="system-msg-credit-copy" />}
      <ChatActionBar entryId={entryId} current={isActive} label="Credits and planning">
        <button
          className="ai-overview-action-btn ai-overview-action-btn--add-credit system-msg-review-btn system-msg-review-btn--add-credit"
          onClick={() => {
            void recordUserActionClick(
              entryId, phase, 'addcredit', 'Add Credits', projectId, sessionId,
            );
            void recordActionClick(entryId, phase, 'addcredit', projectId, sessionId);
            setActiveBillingView('credits');
          }}
          type="button"
        >
          <i className="fa-solid fa-coins" /> Add Credits
        </button>
        <button
          className="ai-overview-action-btn ai-overview-action-btn--upgrade system-msg-review-btn system-msg-review-btn--upgrade"
          onClick={() => {
            void recordUserActionClick(
              entryId, phase, 'upgrade', 'Upgrade', projectId, sessionId,
            );
            void recordActionClick(entryId, phase, 'upgrade', projectId, sessionId);
            setActiveBillingView('plans');
          }}
          type="button"
        >
          <i className="fa-solid fa-arrow-up" /> Upgrade
        </button>
        <button
          className="ai-overview-action-btn ai-overview-action-btn--continue system-msg-review-btn system-msg-review-btn--continue"
          disabled={!continueEnabled}
          aria-busy={loading || undefined}
          onClick={handleContinue}
          type="button"
        >
          {loading ? <><BrandPendulumMark animated={false} className="review-btn-spinner" /> Continuing...</> : <><i className="fa-solid fa-play" /> {continueLabel}</>}
        </button>
      </ChatActionBar>
      {activeBillingView && (
        <BillingModal
          source="planning:credit-gate"
          initialView={activeBillingView}
          initialCreditAmount={getTopUpCreditAmount({
            shortfall: designCreditShortfall(designCredit) ?? phaseBuild.state?.credit_shortfall,
          }) ?? 1}
          onClose={() => setActiveBillingView(null)}
        />
      )}
    </>
  );
};

const ContinuedBtn: React.FC<{ button?: SupervisorButton }> = ({ button }) => (
  <button
    className="ai-overview-action-btn ai-overview-action-btn--selected system-msg-review-btn system-msg-review-btn--continue system-msg-review-btn--selected"
    disabled
    type="button"
  >{button?.doneLabel || button?.done_label || '✓ Continued'}</button>
);

const SystemActionButtons: React.FC<{
  projectId?: string | null;
  sessionId?: string | null;
  entryId?: string;
  metadata?: ActionMetadata | null;
  isActive?: boolean;
  wasContinued?: boolean;
}> = ({ projectId, sessionId, entryId, metadata = null, isActive = false, wasContinued = false }) => {
  const toast = useToast();
  const projectActions = useProjectActionButtonHandlers(projectId);
  const [submittingActionKey, setSubmittingActionKey] = useState<string | null>(null);
  const phase = getActionGatePhase(metadata);
  const buttons = getStructuredActionButtons(metadata);
  const hasBuildAction = buttons.some((button) => (
    button.key === 'build' || button.key === 'start_build' || button.action === 'start_build'
  ));
  const phaseBuild = usePhaseBuildState(hasBuildAction ? projectId : null);
  const clickedButtons = getClickedActionButtons(metadata);
  const continued = wasContinued
    || clickedButtons.includes('continue')
    || clickedButtons.includes('credit_continue');

  if (!phase) return null;
  const planningChecklist = (
    <PlanningActionTasks projectId={projectId} entryId={entryId}
      phase={phase} buttons={buttons} current={isActive} createdAt={metadata?.created_at} />
  );

  if (buttons.length === 0) return planningChecklist;
  if (phase === 'insufficient_credits') {
    return (
      <>
        {planningChecklist}
        <CreditButtons
          projectId={projectId}
          sessionId={sessionId}
          entryId={entryId}
          phase={phase}
          buttons={buttons}
          isActive={isActive}
          wasContinued={continued}
        />
      </>
    );
  }
  const recordInvocation = (buttonKey: string, displayedLabel: string, options?: RecordUserActionOptions) => {
    if (options?.required || /^retry\b/i.test(displayedLabel)) {
      return recordUserActionClick(
        entryId, phase, buttonKey, displayedLabel, projectId, sessionId, { ...options, required: true },
      ).catch((error) => {
        toast.error(options?.planningSelection
          ? 'The selected work could not be prepared. Please try again.'
          : 'Retry could not be prepared. Please try again.');
        throw error;
      });
    }

    return recordUserActionClick(
      entryId, phase, buttonKey, displayedLabel, projectId, sessionId,
    );
  };
  const record = (buttonKey: string, displayedLabel: string) => {
    void recordInvocation(buttonKey, displayedLabel);
    void recordActionClick(entryId, phase, buttonKey, projectId, sessionId);
  };

  const handleNavigate = (button: SupervisorButton) => {
    const pathTemplate = button.payload?.path_template;
    const path = button.payload?.path;
    const tab = button.payload?.tab;

    if (typeof tab === 'string' && tab) {
      window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: tab }));
      if (typeof button.payload?.view === 'string') {
        window.dispatchEvent(new globalThis.CustomEvent('switch_tasks_view', {
          detail: button.payload.view,
        }));
      }
    } else if (typeof pathTemplate === 'string' && projectId) {
      window.location.assign(pathTemplate.replace('{project_id}', projectId));
    } else if (typeof path === 'string') {
      window.location.assign(path);
    }
  };

  const handleActionButtonClick = async (button: SupervisorButton) => {
    if (!projectId || submittingActionKey) return;
    setSubmittingActionKey(button.key);
    try {
      await recordUserActionClick(
        entryId, phase, button.key, button.label, projectId, sessionId,
      );
      if (button.action === 'download_code') {
        const download = await projectService.downloadProjectCode(projectId);
        saveBlob(download.blob, download.filename || getCodeZipFilename(projectId));
      } else if (button.action === 'navigate') {
        handleNavigate(button);
      } else if (isProjectUtilityAction(button.action)) {
        await projectActions.handleProjectActionButton(button);
      }
      await recordActionClick(entryId, phase, button.key, projectId, sessionId);
    } catch (error) {
      toast.error(errorMessage(error, `We couldn’t run “${button.label}”. Refresh and try again.`));
    } finally {
      setSubmittingActionKey(null);
    }
  };

  const rendered = buttons.map((button) => {
    if (button.action === 'run_artifact_updates') {
      return <ArtifactUpdatesAction key={button.key} projectId={projectId} sessionId={sessionId}
        entryId={entryId} button={button} current={isActive} />;
    }
    if (button.key === 'specify_edits') {
      return <ReviewBtn key={button.key} phase={phase} onClickStart={(label) => record(button.key, label)} />;
    }
    if (button.key === 'upgrade') {
      return <GateUpgradeButton key={button.key} source="planning:message-quota-gate"
        label="Upgrade now" onRecord={() => record(button.key, 'Upgrade now')} />;
    }
    if (button.key === 'build' || button.key === 'start_build' || button.action === 'start_build') {
      return (
        <BuildBtn
          key={button.key}
          projectId={projectId}
          showStatus={false}
          onInvocation={(label) => {
            void recordUserActionClick(
              entryId, phase, button.key, label, projectId, sessionId,
            );
          }}
          onBuildStart={() => {
            void recordActionClick(entryId, phase, button.key, projectId, sessionId);
          }}
        />
      );
    }
    if (button.key === 'continue' || button.key === 'credit_continue') {
      if (continued) return <ContinuedBtn key={button.key} button={button} />;
      return isActive ? (
        <ContinueBtn
          key={button.key}
          phase={phase}
          button={button}
          entryId={entryId}
          projectId={projectId}
          sessionId={sessionId}
          current={isActive}
          onClickStart={(label, options) => recordInvocation(button.key, label, options)}
        />
      ) : null;
    }
    if (button.kind === 'action') {
      const loading = submittingActionKey === button.key
        || (button.action === 'trigger_stage_deploy' && projectActions.deploying);
      const label = loading ? loadingLabelForSupervisorButton(button) : button.label;

      return (
        <button
          key={button.key}
          className={[
            'ai-overview-action-btn',
            'system-msg-review-btn',
            'system-msg-review-btn--supervisor-action',
            `system-msg-review-btn--${actionClassForSupervisorButton(button)}`,
          ].join(' ')}
          disabled={loading || !projectId}
          onClick={() => { void handleActionButtonClick(button); }}
          type="button"
          aria-busy={loading}
        >
          {loading
            ? <BrandPendulumMark animated={false} className="review-btn-spinner" />
            : iconClassForSupervisorButton(button) && <i className={iconClassForSupervisorButton(button)} aria-hidden="true" />}
          {label}
        </button>
      );
    }
    return null;
  }).filter(Boolean);

  if (rendered.length === 0) return planningChecklist;

  return (
    <>
      {planningChecklist}
      {hasBuildAction && <PhaseBuildStatus source="planning:action-phase-build" state={phaseBuild.state} compact />}
      {phase && QUOTA_QUEUE_PHASES.has(phase)
        && <QuotaResetLine postedAt={metadata?.created_at} phase={phase} current={isActive} />}
      <ChatActionBar entryId={entryId} current={isActive}>{rendered}</ChatActionBar>
      {projectActions.modals}
    </>
  );
};

const extractPageName = (content: string): string | undefined => {
  // Match "done with <PageName>" — stop before elapsed time suffix " in M:SS" or period
  const pageMatch = content.match(/done with\s+(.+?)(?:\s+in\s+\d|\.)/i);

  return pageMatch ? pageMatch[1].trim() : undefined;
};

const getTabForSystemMessage = (content: string): { tab: string; subTab?: string; pageName?: string } | null => {
  for (const { pattern, tab, subTab, extractPage } of SYSTEM_MSG_TAB_MAP) {
    if (pattern.test(content)) {
      return { tab, subTab, pageName: extractPage ? extractPageName(content) : undefined };
    }
  }

  return null;
};

// System notification message (styled like Tech Lead Agent)
const SystemMessage: React.FC<{
  projectId?: string | null;
  sessionId?: string | null;
  messageId?: string;
  content: string;
  isActive?: boolean;
  actionMetadata?: ActionMetadata | null;
}> = ({ projectId, sessionId, messageId, content, isActive = false, actionMetadata = null }) => {
  const match = getTabForSystemMessage(content);
  const actionPhase = getActionGatePhase(actionMetadata);
  const tone = systemMessageTone(content, actionPhase);
  const toneClass = tone === 'green'
    ? ' planning-message--artifact-green'
    : tone === 'orange' ? ' planning-message--artifact-orange' : '';

  const handleView = () => {
    if (!match) return;
    trackViewAgentResult(content);
    window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: match.tab }));
    if (match.subTab) {
      setTimeout(() => {
        window.dispatchEvent(new globalThis.CustomEvent('switch_design_subtab', { detail: match.subTab }));
        if (match.pageName) {
          window.dispatchEvent(new globalThis.CustomEvent('select_design_page', { detail: match.pageName }));
        }
      }, 50);
    }
  };

  return (
    <div className={`planning-message${toneClass}`}>
      <PlanningMessageIdentity name="Tech Lead Agent" />
      <div
        className={`system-msg-body${match && !actionPhase ? ' system-msg-body--clickable' : ''}`}
        onClick={match && !actionPhase ? handleView : undefined}
        role={match && !actionPhase ? 'button' : undefined}
        tabIndex={match && !actionPhase ? 0 : undefined}
        onKeyDown={match && !actionPhase ? (e) => { if (e.key === 'Enter') handleView(); } : undefined}
      >
        <SystemMessageText content={content} showSpinner={isWarmupStartMessage(content) && isActive} />
        {actionPhase
          ? (
            <SystemActionButtons
              projectId={projectId}
              sessionId={sessionId}
              entryId={messageId}
              metadata={actionMetadata}
              isActive={isActive}
            />
          )
          : match && (
            <span className="system-msg-chevron" aria-hidden="true">›</span>
          )}
      </div>
    </div>
  );
};

// Extracted to satisfy max-lines-per-function ESLint rule
const GroupedSystemMessageItem: React.FC<{
  m: ChatMessage; isActive?: boolean; wasContinued?: boolean; showWarmupSpinner?: boolean;
}> = ({ m, isActive = false, wasContinued = false, showWarmupSpinner = false }) => {
  const match = getTabForSystemMessage(m.content);
  const actionPhase = getActionGatePhase(m);

  const handleView = () => {
    if (!match) return;
    trackViewAgentResult(m.content);
    window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: match.tab }));
    if (match.subTab) {
      setTimeout(() => {
        window.dispatchEvent(new globalThis.CustomEvent('switch_design_subtab', { detail: match.subTab }));
        if (match.pageName) {
          window.dispatchEvent(new globalThis.CustomEvent('select_design_page', { detail: match.pageName }));
        }
      }, 50);
    }
  };

  const isClickable = match && !actionPhase;

  return (
    <div
      className={`system-msg-body${isClickable ? ' system-msg-body--clickable' : ''}`}
      onClick={isClickable ? handleView : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter') handleView(); } : undefined}
    >
      <SystemMessageText content={m.content} showSpinner={showWarmupSpinner} />
      {isActive && !wasContinued && !actionPhase && isCreditBlockedMessage(m.content) && (
        <CreditRefillNotice projectId={m.project_id} />
      )}
      {actionPhase
        ? (
          <SystemActionButtons
            projectId={m.project_id}
            sessionId={m.session_id}
            entryId={m.id}
            metadata={m}
            isActive={isActive}
            wasContinued={wasContinued}
          />
        )
        : match && (
          <span className="system-msg-chevron" aria-hidden="true">›</span>
        )}
    </div>
  );
};

// Check if a group should be merged: extract review gate into its own bubble
const shouldMergeGroup = (msgs: ChatMessage[]): {
  merged: boolean;
  reviewMsg: ChatMessage | null;
  buildMsg: ChatMessage | null;
  rest: ChatMessage[];
} => {
  const reviewMsg = msgs.find((m) => !!getDesignReviewPhase(m)) || null;
  const buildMsg = msgs.find((m) => getActionGatePhase(m) === 'review_build') || null;
  const merged = !!reviewMsg || !!buildMsg;
  const consumed = new Set([reviewMsg?.id, buildMsg?.id].filter(Boolean));
  const rest = merged ? msgs.filter((m) => !consumed.has(m.id)) : [];

  return { merged, reviewMsg, buildMsg, rest };
};

// Combined review gate + Review/Build buttons in one body (separate bubble)
const MergedReviewBubble: React.FC<{
  reviewMsg: ChatMessage; isActive?: boolean; wasContinued?: boolean;
}> = ({ reviewMsg, isActive = false, wasContinued = false }) => {
  return (
    <div className={`planning-message${systemMessageToneClass([reviewMsg])}`}>
      <PlanningMessageIdentity name="Tech Lead Agent" />
      <div className="system-msg-body">
        <SystemMessageText content={reviewMsg.content} />
      </div>
      <SystemActionButtons
        projectId={reviewMsg.project_id}
        sessionId={reviewMsg.session_id}
        entryId={reviewMsg.id}
        metadata={reviewMsg}
        isActive={isActive}
        wasContinued={wasContinued}
      />
    </div>
  );
};

// Grouped system messages — multiple consecutive system messages in one bubble
// When the group contains a review + build merge, the review breaks into its own separate bubble.
const GroupedSystemMessage: React.FC<{
  messages: ChatMessage[]; inlineButtons?: React.ReactNode; isLastGroup?: boolean; wasContinued?: boolean;
}> = (
  { messages, inlineButtons, isLastGroup = false, wasContinued = false },
) => {
  const { merged, reviewMsg, buildMsg, rest } = shouldMergeGroup(messages);
  const restGroup = rest.length > 0 ? (
    <div className={`planning-message${systemMessageToneClass(rest)}`}>
      <PlanningMessageIdentity name="Tech Lead Agent" />
      <div className="system-msg-group">
        {rest.map((m, i) => (
          <GroupedSystemMessageItem
            key={m.id}
            m={m}
            showWarmupSpinner={shouldShowWarmupSpinner(rest, i, isLastGroup)}
          />
        ))}
      </div>
    </div>
  ) : null;

  if (merged) {
    return (
      <>
        {restGroup}
        {reviewMsg && <MergedReviewBubble reviewMsg={reviewMsg} isActive={isLastGroup} wasContinued={wasContinued} />}
        {buildMsg && (
          <div className={`planning-message${systemMessageToneClass([buildMsg])}`}>
            <PlanningMessageIdentity name="Tech Lead Agent" />
            <div className="system-msg-body">
              <SystemMessageText content={buildMsg.content} />
            </div>
            <SystemActionButtons
              projectId={buildMsg.project_id}
              sessionId={buildMsg.session_id}
              entryId={buildMsg.id}
              metadata={buildMsg}
              isActive={isLastGroup}
              wasContinued={wasContinued}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`planning-message${systemMessageToneClass(messages)}`}>
      <PlanningMessageIdentity name="Tech Lead Agent" />
      <div className="system-msg-group">
        {messages.map((m, i) => (
          <GroupedSystemMessageItem
            key={m.id} m={m}
            isActive={isLastGroup && i === messages.length - 1}
            wasContinued={wasContinued}
            showWarmupSpinner={shouldShowWarmupSpinner(messages, i, isLastGroup)}
          />
        ))}
      </div>
      {inlineButtons}
    </div>
  );
};

// Individual chat message with arrow toggle
interface ChatMsgBoxProps {
  messageId?: string;
  displayName: string;
  content: string;
  isUser: boolean;
  isSystem?: boolean;
  actionMetadata?: ActionMetadata | null;
  progress?: number | null;
  statusSteps?: DeveloperStatusStep[];
  projectId?: string | null;
  sessionId?: string | null;
  jobId?: string | null;
  developerStatus?: string | null;
  mediaUrls?: string[] | null;
  webSearches?: WebSearchActivity[] | null;
  isLast?: boolean;
  isLatest?: boolean;
  inlineButtons?: React.ReactNode;
  showWarmupSpinner?: boolean;
}

// eslint-disable-next-line max-statements
const ChatMsgBox: React.FC<ChatMsgBoxProps> = ({
  messageId,
  displayName,
  content,
  isUser,
  isSystem,
  actionMetadata,
  progress,
  statusSteps,
  projectId,
  sessionId,
  jobId,
  developerStatus,
  mediaUrls,
  webSearches,
  isLast,
  isLatest,
  inlineButtons,
  showWarmupSpinner = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(isLast));
  const [lightboxData, setLightboxData] = useState<{ url: string; type: string } | null>(null);
  const hasDiagram = containsRichDiagram(content);
  const { isTruncated } = truncateWords(content, WORD_LIMIT);
  const signedUrls = useMediaBlobUrls(mediaUrls ?? []);
  const progressStep = useMemo(() => (
    progress !== null && progress !== undefined && !isUser
      ? currentDeveloperStatusStep(statusSteps, developerStatus)
      : undefined
  ), [developerStatus, isUser, progress, statusSteps]);
  const hasRunningProgressStep = Boolean(progressStep && !isTerminalDeveloperStatus(developerStatus) && isRunningStep(progressStep));
  const showProcessingMark = progress === null || progress === undefined || isUser
    ? true
    : (!isTerminalDeveloperStatus(developerStatus) && (progress < 100 || hasRunningProgressStep));
  const progressContent = progress !== null && progress !== undefined && !isUser
    ? resolvedProgressSummaryText(progressStep, content, developerStatus)
    : content;
  const isProgressTerminal = progress !== null && progress !== undefined && !isUser
    && (isTerminalDeveloperStatus(developerStatus) || (!hasRunningProgressStep && progress >= 100));
  const progressMessageClass = [
    'planning-message planning-message--thinking bubble bubble--assistant bubble--thinking bubble--sync-progress',
    isProgressTerminal ? 'bubble--sync-terminal' : '',
  ].filter(Boolean).join(' ');

  // Auto-expand when becoming last, auto-collapse when no longer last
  useEffect(() => {
    setIsExpanded(Boolean(isLast));
  }, [isLast]);

  if (isSystem) {
    return (
      <SystemMessage
        projectId={projectId}
        sessionId={sessionId}
        messageId={messageId}
        content={content}
        actionMetadata={actionMetadata}
      />
    );
  }

  if (progress !== null && progress !== undefined && !isUser) {
    return (
      <div
        className={progressMessageClass}
        data-message-id={messageId}
      >
        <PlanningMessageIdentity className="bubble__meta" name={displayName} />
        <div className="bubble__content bubble__content--processing">
          <div className="bubble-processing-row">
            {showProcessingMark && <BrandPendulumMark animated={false} className="bubble__typing-mark" />}
            <span className="bubble-processing-copy">
              <span className="bubble-processing-row__text">{progressContent}</span>
              <SyncProgress
                progress={progress}
                text={progressContent}
                steps={statusSteps}
                projectId={projectId}
                sessionId={sessionId}
                jobId={jobId}
                developerStatus={developerStatus}
              />
            </span>
          </div>
        </div>
      </div>
    );
  }

  const showWarmupAgentSpinner = showWarmupSpinner && !isUser && isWarmupStartMessage(content);
  const collapsed = isTruncated && !hasDiagram && !isExpanded && !showWarmupAgentSpinner;
  const assistantActionButtons = !isUser && getActionGatePhase(actionMetadata) ? isLatest ? (
    <SystemActionButtons
      projectId={projectId}
      sessionId={sessionId}
      entryId={messageId}
      metadata={actionMetadata}
      isActive
    />
  ) : (
    <PlanningActionTasks projectId={projectId} entryId={messageId} phase={actionMetadata?.phase}
      buttons={getStructuredActionButtons(actionMetadata)} current={false} createdAt={actionMetadata?.created_at} />
  ) : null;

  return (
    <div className={`planning-message ${isUser ? 'user' : ''}`}>
      <PlanningMessageIdentity kind={isUser ? 'user' : 'ai'} name={displayName} />
      {mediaUrls && mediaUrls.length > 0 && (
        <div className="planning-message__media">
          {mediaUrls.map((path) => {
            const ext = path.split('.').pop()?.toLowerCase() || '';
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

            if (isImage) {
              const blobSrc = signedUrls[path];

              return (
                <div key={path} className="planning-message__media-img-wrapper">
                  {blobSrc ? (
                    <img
                      src={blobSrc}
                      alt="attached media"
                      className="planning-message__media-img"
                      loading="lazy"
                      onClick={() => setLightboxData({ url: blobSrc, type: 'image' })}
                    />
                  ) : (
                    <div className="planning-message__media-img planning-message__media-img--loading" />
                  )}
                  {!blobSrc && (
                    <div className="media-preview-overlay">
                      <svg className="media-preview-spinner" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="media-preview-spinner-circle"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            }

            if (!isImage) {
              /* Storage key format: chat_media/projId/{8hexchars}_{original_name}
                 Strip the UUID prefix to recover the original filename. */
              const rawName = path.split('/').pop() || path;
              const displayName = /^[0-9a-f]{8}_/.test(rawName) ? rawName.slice(9) : rawName;
              const extUpper = ext.toUpperCase();
              const colorMap: Record<string, string> = {
                PDF: '#ef4444',
                DOCX: '#3b82f6',
                DOC: '#3b82f6',
                PPTX: '#f97316',
                TXT: '#a78bfa',
              };
              const badgeColor = colorMap[extUpper] || '#9ca3af';
              const blobUrl = signedUrls[path];

              const handleDocClick = () => {
                if (!blobUrl) return;
                const canPreview = ['pdf', 'txt', 'md'].includes(ext);
                if (canPreview) {
                  setLightboxData({ url: blobUrl, type: ext });
                } else {
                  window.open(blobUrl, '_blank');
                }
              };

              return (
                <div
                  key={path}
                  className={`planning-message__media-doc${blobUrl ? ' planning-message__media-doc--clickable' : ''}`}
                  onClick={handleDocClick}
                  role={blobUrl ? 'button' : undefined}
                  tabIndex={blobUrl ? 0 : undefined}
                  onKeyDown={blobUrl ? (e) => { if (e.key === 'Enter') handleDocClick(); } : undefined}
                  style={{ '--doc-color': badgeColor } as React.CSSProperties}
                >
                  <div className="planning-message__media-doc-icon">
                    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        stroke="currentColor" strokeWidth="1.5" />
                      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div className="planning-message__media-doc-info">
                    <span className="planning-message__media-doc-name" title={displayName}>
                      {displayName.length > 24 ? `${displayName.slice(0, 22)}…` : displayName}
                    </span>
                    <span className="planning-message__media-doc-ext">
                      {extUpper}
                    </span>
                  </div>
                </div>
              );
            }

            return <div key={path} className="media-placeholder" />;
          })}
        </div>
      )}
      {lightboxData && <ChatMediaLightbox url={lightboxData.url} type={lightboxData.type} onClose={() => setLightboxData(null)} />}
      {!isUser && <WebSearchActivityList searches={webSearches} />}
      {showWarmupAgentSpinner ? (
        <div className="agent-warmup-row">
          <BrandPendulumMark animated={false} className="bubble__typing-mark system-msg-warmup-mark" />
          <ContentWrap collapsed={false}>
            <MarkdownContent content={content} />
          </ContentWrap>
        </div>
      ) : (
        <ContentWrap collapsed={collapsed}>
          <MarkdownContent content={content} />
        </ContentWrap>
      )}
      {isTruncated && !hasDiagram && !showWarmupAgentSpinner && !(isLast && isExpanded) && (
        <ExpandToggle isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
      )}
      {assistantActionButtons}
      {!isUser && isLatest && !getActionGatePhase(actionMetadata) && isCreditBlockedMessage(content) && (
        <CreditRefillNotice projectId={projectId} />
      )}
      {inlineButtons}
    </div>
  );
};

// Message item wrapper
interface ExtendedMessageItemProps extends MessageItemProps {
  isLast?: boolean;
  isLatest?: boolean;
  inlineButtons?: React.ReactNode;
  planStates?: Map<string, DeveloperPlanState>;
  showWarmupSpinner?: boolean;
}

// eslint-disable-next-line max-statements
const MessageItem: React.FC<ExtendedMessageItemProps> = ({
  message, userDisplayName, isLast, isLatest, inlineButtons, planStates, showWarmupSpinner,
}) => {
  const displayName = getDisplayName(message, userDisplayName);
  const envelope = parsePlanEnvelope(message.content);
  const visibleContent = envelope ? planEnvelopeVisibleMarkdown(envelope) : message.content;
  // Only the developer_plan envelope renders Explain / Approve / Deny — state
  // and result envelopes update the latest state via planStates but don't
  // attach their own action buttons.
  const planButtons = (
    envelope?.kind === 'developer_plan'
    && message.project_id
    && message.session_id
  )
    ? (
      <DevPlanButtons
        envelope={envelope}
        liveState={planStates?.get(envelope.plan_id)}
        projectId={message.project_id}
        sessionId={message.session_id}
        entryId={message.id}
        variant="actionBar"
      />
    )
    : null;
  const combinedButtons = planButtons
    ? <>{planButtons}{inlineButtons}</>
    : inlineButtons;

  return (
    <ChatMsgBox
      messageId={message.id}
      displayName={displayName}
      content={visibleContent}
      isUser={message.author === 'user'}
      isSystem={message.author === 'system'}
      actionMetadata={message}
      progress={message.progress}
      statusSteps={message.developer_status_steps}
      projectId={message.project_id}
      sessionId={message.session_id}
      jobId={message.developer_job_id}
      developerStatus={message.developer_status}
      mediaUrls={message.media_urls}
      webSearches={message.tool_activity}
      isLast={isLast}
      isLatest={isLatest}
      inlineButtons={combinedButtons}
      showWarmupSpinner={showWarmupSpinner}
    />
  );
};

// Check whether the just-pushed message should end the current group
const isGroupEnd = (message: ChatMessage) => (
  !!getDesignReviewPhase(message) || getActionGatePhase(message) === 'review_build'
);

// Check if a system message starts a fresh planning run (should begin a new group)
const isRunStart = (content: string) => isPlanningStartMessage(content);

// PM status messages that should be suppressed from chat (shown in tasks panel instead)
const isPmStatusMessage = (content: string) => /^Project Manager is done/i.test(content);

// Collect consecutive system messages, but break at planning boundaries
// eslint-disable-next-line max-statements
function collectSystemGroup(messages: ChatMessage[], start: number): { group: ChatMessage[]; next: number } {
  const group: ChatMessage[] = [];
  let i = start;
  let hasEncounteredGroupEnd = false;

  while (i < messages.length && messages[i].author === 'system') {
    // Skip PM status messages — these are shown in the tasks panel progress bar instead
    if (isPmStatusMessage(messages[i].content)) { i++; continue; }
    if (group.length > 0 && isRunStart(messages[i].content)) break;

    // If a previous message was a review gate and this new message isn't a log message,
    // it probably shouldn't blindly join. However, since we want async logs like
    // "Design Agent is done with X" to remain attached above the review gate, we keep collecting.
    // If we already hit a review gate AND the new message is ALSO a review gate, we break.
    if (hasEncounteredGroupEnd && isGroupEnd(messages[i])) break;

    group.push(messages[i]);

    if (isGroupEnd(messages[i])) {
      hasEncounteredGroupEnd = true;
    }

    i++;
  }

  return { group, next: i };
}

// Detect if a review gate in this group was followed by a "Continue planning..." message
const detectContinued = (group: ChatMessage[], remaining: ChatMessage[]): boolean => (
  group.some((m) => !!getDesignReviewPhase(m))
    && remaining.some((m) => /^Continue planning/i.test(m.content))
);

// Process a single index: either push a system group or a normal message item
// eslint-disable-next-line max-statements
function processMessageAt(
  messages: ChatMessage[],
  i: number,
  userDisplayName: string,
  elements: React.ReactElement[],
  inlineButtons?: React.ReactNode,
  planStates?: Map<string, DeveloperPlanState>,
  inlineButtonsForMessage?: (
    message: ChatMessage,
    fallback: React.ReactNode | undefined,
  ) => React.ReactNode | undefined,
): number {
  if (messages[i].author === 'system' && isWarmupLifecycleMessage(messages[i].content)) {
    const isLast = i >= messages.length - 5;
    const isLatest = i === messages.length - 1;

    elements.push(
      <MessageItem
        key={messages[i].id}
        message={asTechLeadLifecycleMessage(messages[i])}
        userDisplayName={userDisplayName}
        isLast={isLast}
        isLatest={isLatest}
        inlineButtons={inlineButtonsForMessage
          ? inlineButtonsForMessage(messages[i], isLast ? inlineButtons : undefined)
          : (isLast ? inlineButtons : undefined)}
        planStates={planStates}
        showWarmupSpinner={shouldShowAgentWarmupSpinner(messages, i)}
      />,
    );

    return i + 1;
  }

  if (messages[i].author === 'system') {
    const { group, next } = collectSystemGroup(messages, i);

    // A run of nothing but skipped PM-status rows yields an empty group;
    // rendering it would throw on group[0].id, so drop it entirely.
    if (group.length === 0) return next;

    const wasContinued = detectContinued(group, messages.slice(next));
    // Review gate groups: isActive = !wasContinued (button persists on refresh)
    // Non-review groups: isActive only when truly the last group
    const hasReviewGate = group.some((m) => !!getDesignReviewPhase(m));
    const isLast = next >= messages.length;

    elements.push(
      <GroupedSystemMessage
        key={group[0].id}
        messages={group}
        inlineButtons={isLast ? inlineButtons : undefined}
        isLastGroup={hasReviewGate ? !wasContinued : isLast}
        wasContinued={wasContinued}
      />,
    );

    return next;
  }

  const isLast = i >= messages.length - 5;
  const isLatest = i === messages.length - 1;
  const fallbackInlineButtons = isLast && messages[i].author !== 'user' ? inlineButtons : undefined;

  elements.push(
    <MessageItem
      key={messages[i].id} message={messages[i]} userDisplayName={userDisplayName}
      isLast={isLast}
      isLatest={isLatest}
      inlineButtons={inlineButtonsForMessage
        ? inlineButtonsForMessage(messages[i], fallbackInlineButtons)
        : fallbackInlineButtons}
      planStates={planStates}
      showWarmupSpinner={shouldShowAgentWarmupSpinner(messages, i)}
    />,
  );

  return i + 1;
}

// Group consecutive system messages, leave others as-is
function buildMessageElements(
  messages: ChatMessage[],
  userDisplayName: string,
  inlineButtons?: React.ReactNode,
  planStates?: Map<string, DeveloperPlanState>,
  inlineButtonsForMessage?: (
    message: ChatMessage,
    fallback: React.ReactNode | undefined,
  ) => React.ReactNode | undefined,
): React.ReactElement[] {
  const elements: React.ReactElement[] = [];
  let i = 0;

  while (i < messages.length) {
    i = processMessageAt(
      messages,
      i,
      userDisplayName,
      elements,
      inlineButtons,
      planStates,
      inlineButtonsForMessage,
    );
  }

  return elements;
}

/* ---------- Inline action buttons for last AI message ---------- */

/** Click the toolbar button so both share the same state/trigger. */

/* ---------- Supervisor-driven agent suggestion pills ---------- */

export interface SuggestedAgentState {
  agents: string[];
  buttons: SupervisorButton[];
  projectId: string;
  messageId: string;
  createdAt?: string;
  sessionId?: string | null;
  promptScope?: Record<string, unknown> | null;
  aiContent?: string | null;
}

const PlanningActionRegistration: React.FC<{
  projectId: string;
  sessionId?: string | null;
  entryId: string;
  button: SupervisorButton;
  actionIdentity?: string;
  enabled: boolean;
  current: boolean;
  invoke: () => void | Promise<void>;
}> = ({ projectId, sessionId, entryId, button, actionIdentity, enabled, current, invoke }) => {
  useEffect(() => registerPlanningAction({
    projectId,
    sessionId: sessionId ?? null,
    entryId: actionIdentity ?? entryId,
    buttonKey: button.key,
    label: button.label,
    enabled,
    current,
    invoke,
  }), [actionIdentity, button.key, button.label, current, enabled, entryId, invoke, projectId, sessionId]);

  return null;
};

const getTriggerAgentErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: unknown } } }).response;
    if (typeof response?.data?.detail === 'string' && response.data.detail.trim()) {
      return response.data.detail;
    }
  }

  if (error instanceof Error && error.message.trim()) return error.message;

  return 'Could not start the selected agents.';
};

const getCodeZipFilename = (projectId: string) => `ma-project-${projectId.slice(0, 8)}.zip`;

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const buttonPayloadAgents = (button: SupervisorButton): string[] => {
  const agents = button.payload?.agents;
  return Array.isArray(agents) ? agents.filter((agent): agent is string => typeof agent === 'string') : [];
};

const triggerAgentsOnServer = async (
  agents: string[],
  suggestion: SuggestedAgentState,
  planningActionInvocationId?: string,
): Promise<TriggerAgentsResponse> => {
  // start_build is UI-only (toolbar click); don't send it to the API
  const apiAgents = agents.filter((a) => a !== 'start_build');

  if (apiAgents.length > 0) {
    return chatService.triggerAgents(
      suggestion.projectId,
      apiAgents,
      suggestion.messageId,
      suggestion.sessionId,
      suggestion.promptScope ?? null,
      planningActionInvocationId,
    );
  }

  return { status: 'triggered', agents: [] };
};

const BuildPill: React.FC<{
  projectId?: string | null;
  sessionId?: string | null;
  entryId?: string;
  onBeforeClick?: () => void;
}> = ({ projectId, sessionId, entryId, onBeforeClick }) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const clickIdRef = useRef<string | null>(null);
  const phaseBuild = usePhaseBuildState(projectId);
  const phase = phaseBuild.state?.current_phase;
  const creditShortfall = getPhaseActionCreditShortfall(phaseBuild.state);
  const buildEnabled = isPhaseBuildActionEnabled(phaseBuild.state);

  // Re-enable the button once the build flow finishes (success or error),
  // so the user can trigger builds for multiple tasks without reloading.
  useBuildResolvedListener(clickIdRef, setLoading);

  const handleClick = () => {
    if (loading) return;
    void recordUserActionClick(
      entryId,
      'agent_suggestion',
      'start_build',
      getPhaseBuildLabel(phaseBuild.state, phaseBuild.isLoading),
      projectId,
      sessionId,
    );
    if (creditShortfall > 0) {
      signalInsufficientCredits(creditShortfall);

      return;
    }
    if (phaseBuild.state?.can_start && checkTierGate(user, setShowUpgradeModal)) return;
    onBeforeClick?.();
    setLoading(true);
    trackStartBuildingClick('agent_pill');
    if (phaseBuild.state?.can_start) {
      void recordActionClick(entryId, 'agent_suggestion', 'start_build', projectId, sessionId);
    }
    dispatchStartBuild(clickIdRef, phase?.id);
  };

  return (
    <>
      <button
        className={`agent-suggestion-pill agent-suggestion-pill--build${loading ? ' agent-suggestion-pill--triggered' : ''}`}
        onClick={handleClick}
        disabled={
          loading
          || phaseBuild.isLoading
          || (!buildEnabled && creditShortfall <= 0)
        }
        title={getPhaseBuildTitle(phaseBuild.state)}
        type="button"
      >
        {loading
          ? `⟳ ${getPhaseBuildBusyLabel(phaseBuild.state)}`
          : getPhaseBuildLabel(phaseBuild.state, phaseBuild.isLoading)}
      </button>
      {showUpgradeModal && <UpgradeModal source="planning:phase-build" onClose={() => setShowUpgradeModal(false)} />}
    </>
  );
};

const hasActiveDesignGeneration = (pages: DesignPage[]): boolean => (
  pages.some((page) => Boolean(page.generation_in_progress))
);

const isDesignAgentSuggestion = (agents: string[]): boolean => agents.includes('Design Agent');

// Module-level cache of the latest `suggested_agents` event. Needed because
// `AgentSuggestionButtons` now lives inside the overview panel's action bar,
// which may mount AFTER PlanningPage.tsx has already dispatched the restored
// `suggested_agents` event on page load. A local useState would miss that
// event; the cache lets a late-mounting component read the last value.
let latestSuggestion: SuggestedAgentState | null = null;
const historicalActionSuggestionsByMessageId = new Map<string, SuggestedAgentState>();
const suggestionSubscribers = new Set<() => void>();
const notifySuggestionSubscribers = () => suggestionSubscribers.forEach((cb) => cb());

const cacheHistoricalActionSuggestion = (suggestion: SuggestedAgentState): void => {
  if (suggestion.buttons.length === 0) return;
  const cachedSuggestion = {
    ...suggestion,
    agents: [],
    buttons: [...suggestion.buttons],
  };
  historicalActionSuggestionsByMessageId.set(suggestion.messageId, cachedSuggestion);
};

const cachedHistoricalActionSuggestionForMessage = (
  message: ChatMessage,
  projectId?: string | null,
  sessionId?: string | null,
): SuggestedAgentState | null => {
  const resolvedProjectId = projectId ?? message.project_id ?? null;
  const resolvedSessionId = sessionId ?? message.session_id ?? null;
  const byId = historicalActionSuggestionsByMessageId.get(message.id);

  if (byId && suggestionMatchesContext(byId, resolvedProjectId, resolvedSessionId)) return byId;

  return null;
};

const historicalActionSuggestionFromMessage = (
  message: ChatMessage,
  projectId?: string | null,
  sessionId?: string | null,
): SuggestedAgentState | null => {
  const cached = cachedHistoricalActionSuggestionForMessage(message, projectId, sessionId);
  if (cached) {
    return { ...cached, createdAt: message.created_at };
  }

  const structuredButtons = getStructuredActionButtons(message);
  const restorableOnlyStoredButtons = (
    structuredButtons.length > 0 && structuredButtons.every(isRestorableSupervisorButton)
  );
  const buttons = message.phase === 'agent_suggestion'
    ? structuredButtons
    : structuredButtons.filter(isRestorableSupervisorButton);

  if (
    (!message.project_id && !projectId)
    || (message.phase !== 'agent_suggestion' && !restorableOnlyStoredButtons)
  ) {
    return null;
  }
  if (buttons.length === 0 && message.phase !== 'agent_suggestion') return null;

  return {
    agents: [],
    buttons,
    projectId: message.project_id ?? projectId ?? '',
    messageId: message.id,
    createdAt: message.created_at,
    sessionId: message.session_id ?? null,
    promptScope: message.prompt_scope ?? null,
    aiContent: message.content,
  };
};

const useSuggestionStoreVersion = (): number => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const sync = () => setVersion((value) => value + 1);

    suggestionSubscribers.add(sync);

    return () => { suggestionSubscribers.delete(sync); };
  }, []);

  return version;
};

const replaceLatestSuggestionAgents = (
  suggestion: SuggestedAgentState,
  agents: string[],
): void => {
  const nextAgents = [...agents];
  if (suggestion.agents.includes('start_build') && !nextAgents.includes('start_build')) {
    nextAgents.push('start_build');
  }
  latestSuggestion = { ...suggestion, agents: nextAgents };
  cacheHistoricalActionSuggestion(latestSuggestion);
  notifySuggestionSubscribers();
};

// Anchor the suggestion pills to a specific item.id — whatever item was last
// at the time the user interacted with a pill. This keeps the pills from
// "following" new action bars that appear after the click (e.g. when agents
// start running and emit new progress bars). Persisted to localStorage keyed
// by (projectId, messageId) so the anchor survives page reloads — without
// persistence the pills jumped back to isLast on refresh, which was
// confusing for users who'd just clicked them on an earlier bar.
let anchorItemId: string | null = null;
let anchorMessageId: string | null = null;
const anchorSubscribers = new Set<() => void>();
const notifyAnchorSubscribers = () => anchorSubscribers.forEach((cb) => cb());

const anchorStorageKey = (
  projectId: string, messageId: string,
): string => `suggestion-anchor:${projectId}:${messageId}`;

const persistAnchor = (
  projectId: string | null | undefined, messageId: string | null, itemId: string,
): void => {
  if (!projectId || !messageId) return;
  try { localStorage.setItem(anchorStorageKey(projectId, messageId), itemId); }
  catch { /* quota/private mode — best effort */ }
};

const restoreAnchor = (projectId: string, messageId: string): string | null => {
  try { return localStorage.getItem(anchorStorageKey(projectId, messageId)); }
  catch { return null; }
};

export const pinSuggestionAnchor = (
  itemId: string, messageId: string, projectId?: string | null,
): void => {
  if (anchorItemId === itemId && anchorMessageId === messageId) return;
  anchorItemId = itemId;
  anchorMessageId = messageId;
  persistAnchor(projectId ?? latestSuggestion?.projectId ?? null, messageId, itemId);
  notifyAnchorSubscribers();
};

export const useSuggestionAnchor = (): string | null => {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((n) => n + 1);

    anchorSubscribers.add(cb);

    return () => { anchorSubscribers.delete(cb); };
  }, []);

  return anchorItemId;
};

// Signal from AiOverviewPanel → MessagesContainer. When the overview has no
// items, the chat area renders a fallback `AgentSuggestionButtons` so the
// supervisor's plan_all / keep_talking pills remain visible on a fresh
// project before any agent output exists.
let overviewHasItems = false;
const overviewHasItemsSubscribers = new Set<() => void>();

export const setOverviewHasItems = (hasItems: boolean) => {
  if (overviewHasItems === hasItems) return;
  overviewHasItems = hasItems;
  overviewHasItemsSubscribers.forEach((cb) => cb());
};

export const useOverviewHasItems = (): boolean => {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((n) => n + 1);

    overviewHasItemsSubscribers.add(cb);

    return () => { overviewHasItemsSubscribers.delete(cb); };
  }, []);

  return overviewHasItems;
};

const releaseAnchorIfStale = (newMessageId: string) => {
  if (anchorMessageId === newMessageId) return;
  anchorItemId = null;
  anchorMessageId = null;
  notifyAnchorSubscribers();
};

const setAnchor = (itemId: string, messageId: string): void => {
  if (anchorItemId === itemId && anchorMessageId === messageId) return;
  anchorItemId = itemId;
  anchorMessageId = messageId;
  notifyAnchorSubscribers();
};

/** Restore the anchor from localStorage when one was persisted for this
 *  messageId; otherwise clear via the existing stale-release path. */
const applyRestoredAnchor = (projectId: string, messageId: string): void => {
  const restored = restoreAnchor(projectId, messageId);

  if (restored) setAnchor(restored, messageId);
  else releaseAnchorIfStale(messageId);
};

const applySuggestionEvent = (detail: {
  agents: string[];
  buttons?: SupervisorButton[];
  projectId: string;
  messageId: string;
  createdAt?: string;
  sessionId?: string | null;
  promptScope?: Record<string, unknown> | null;
  aiContent?: string | null;
}) => {
  const buttons = detail.buttons ?? [];

  if (detail.agents.length === 0 && buttons.length === 0) {
    if (
      latestSuggestion
      && !suggestionMatchesContext(latestSuggestion, detail.projectId, detail.sessionId ?? null)
    ) {
      return;
    }
    latestSuggestion = null;
    anchorItemId = null;
    anchorMessageId = null;
    notifyAnchorSubscribers();
    notifySuggestionSubscribers();
    return;
  }

  latestSuggestion = {
    agents: detail.agents, buttons, projectId: detail.projectId,
    messageId: detail.messageId, sessionId: detail.sessionId ?? null,
    createdAt: detail.createdAt,
    promptScope: detail.promptScope ?? null,
    aiContent: detail.aiContent,
  };
  cacheHistoricalActionSuggestion(latestSuggestion);
  applyRestoredAnchor(detail.projectId, detail.messageId);
  notifySuggestionSubscribers();
};

const useSuggestedAgents = (
  projectId?: string | null,
  sessionId?: string | null,
  enabled = true,
) => {
  const scopedLatestSuggestion = enabled && suggestionMatchesContext(latestSuggestion, projectId, sessionId)
    ? latestSuggestion
    : null;
  const [suggestion, setSuggestion] = useState<SuggestedAgentState | null>(scopedLatestSuggestion);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const sync = () => {
      setSuggestion(
        suggestionMatchesContext(latestSuggestion, projectId, sessionId) ? latestSuggestion : null,
      );
    };
    const handler = (e: Event) => applySuggestionEvent((e as globalThis.CustomEvent).detail);

    suggestionSubscribers.add(sync);
    window.addEventListener('suggested_agents', handler);
    sync();

    return () => {
      suggestionSubscribers.delete(sync);
      window.removeEventListener('suggested_agents', handler);
    };
  }, [enabled, projectId, sessionId]);

  return { suggestion };
};

const useDesignActionBlock = (projectId?: string | null): { blocked: boolean; loaded: boolean } => {
  const { currentData: data, isLoading, isUninitialized } = useGetDesignPagesQuery(
    { projectId: projectId!, light: true, includePlanned: true },
    { skip: !projectId },
  );

  const blocked = useMemo(
    () => hasActiveDesignGeneration(data?.design_pages ?? []),
    [data],
  );

  return { blocked, loaded: !isLoading && !isUninitialized };
};

const visibleSuggestionButtons = (
  suggestion: SuggestedAgentState | null,
  designActionBlock: { blocked: boolean; loaded: boolean },
): SupervisorButton[] => {
  if (!suggestion || suggestion.buttons.length === 0) return [];

  const blockAgentRuns = designActionBlock.blocked
    || (isDesignAgentSuggestion(suggestion.agents) && !designActionBlock.loaded);

  return suggestion.buttons.filter((button) => (
    !(blockAgentRuns && button.action === 'trigger_agents')
  ));
};

const isRunnableAgentSuggestionButton = (button: SupervisorButton): boolean => (
  button.kind === 'agent' && button.action !== 'focus_chat'
);

export const useAgentSuggestionButtonCount = (
  projectId?: string | null,
  sessionId?: string | null,
): number => {
  const { suggestion } = useSuggestedAgents(projectId, sessionId);
  const designActionBlock = useDesignActionBlock(suggestion?.projectId ?? null);

  return visibleSuggestionButtons(suggestion, designActionBlock).length;
};

export const useAgentRunSuggestionButtonCount = (
  projectId?: string | null,
  sessionId?: string | null,
): number => {
  const { suggestion } = useSuggestedAgents(projectId, sessionId);
  const designActionBlock = useDesignActionBlock(suggestion?.projectId ?? null);

  return visibleSuggestionButtons(suggestion, designActionBlock).filter(isRunnableAgentSuggestionButton).length;
};

export const useSuggestedAgentMessageId = (
  projectId?: string | null,
  sessionId?: string | null,
): string | null => {
  const { suggestion } = useSuggestedAgents(projectId, sessionId);

  return suggestion?.messageId ?? null;
};

/** The unified timeline owns the checklist outside its inline button row. */
export const AgentSuggestionPlanningTasks: React.FC<{
  projectId?: string | null;
  entryId: string;
  createdAt?: string;
  buttons?: readonly SupervisorButton[];
  current?: boolean;
}> = ({ projectId, entryId, createdAt, buttons, current = false }) => (
    <PlanningActionTasks
      projectId={projectId}
      entryId={entryId}
      createdAt={createdAt}
      phase="agent_suggestion"
      buttons={buttons}
      current={current}
    />
);

const focusChatInput = () => {
  window.requestAnimationFrame(() => {
    const textarea = document.querySelector<HTMLTextAreaElement>('.text-input');

    textarea?.focus();
  });
};

interface FlyGhost {
  x: number;
  y: number;
  w: number;
  h: number;
  dx: number;
  dy: number;
}

interface AgentSuggestionButtonsProps {
  autoScrollIntoView?: boolean;
  hidden?: boolean;
  historical?: boolean;
  itemId?: string;
  projectId?: string | null;
  sessionId?: string | null;
  suggestionOverride?: SuggestedAgentState | null;
  /** The unified action bar renders its checklist above the whole button row. */
  showPlanningTasks?: boolean;
  /** Standalone chat surfaces need their own source; overview supplies its shared bar. */
  standaloneActionBar?: boolean;
}

// eslint-disable-next-line max-statements
export const AgentSuggestionButtons: React.FC<AgentSuggestionButtonsProps> = ({
  autoScrollIntoView = true,
  hidden = false,
  historical = false,
  itemId,
  projectId,
  sessionId,
  suggestionOverride = null,
  showPlanningTasks = true,
  standaloneActionBar = false,
}) => {
  const { suggestion: liveSuggestion } = useSuggestedAgents(projectId, sessionId, !suggestionOverride);
  const suggestion = suggestionOverride ?? liveSuggestion;
  const isHistoricalSuggestion = historical || Boolean(suggestionOverride);
  const taskSelection = usePlanningActionSelection({
    projectId: suggestion?.projectId, entryId: suggestion?.messageId,
    phase: 'agent_suggestion', buttons: suggestion?.buttons, current: !isHistoricalSuggestion,
  });
  const designActionBlock = useDesignActionBlock(suggestion?.projectId ?? null);
  const projectActions = useProjectActionButtonHandlers(suggestion?.projectId ?? projectId);
  const toast = useToast();
  const titleConfirmedAt = useAppSelector((state) => state.planning.titleConfirmedAt);
  const keepTalkingBtnRef = useRef<HTMLButtonElement>(null);
  const planningStart = usePlanningStart();
  const titleRequestKey = suggestion
    ? JSON.stringify([suggestion.projectId, suggestion.sessionId ?? null, suggestion.messageId])
    : null;
  const [flyGhost, setFlyGhost] = useState<FlyGhost | null>(null);
  const [creditRefusal, setCreditRefusal] = useState<PlanningCreditRefusalDetail | null>(null);
  // The current suggestion shows the title form ahead of Start, so one click
  // saves the title and starts planning. Only a restored, already-clicked
  // Start (no draft owner) still hands the decision to the form's own action.
  const ownsInlineTitle = Boolean(
    planningStart.confirmation?.inline && planningStart.confirmation.requestKey === titleRequestKey,
  );
  // Title confirmation owns the next decision across every projection of the
  // suggestion controls. Hiding only the exact source leaves a stale
  // Keep Talking / Start pair available in the sticky dock.
  const showTitleModal = Boolean(planningStart.pending?.needsTitle) && !ownsInlineTitle;
  // Inline, a pending Start that still waits for its title is not "starting"
  // yet: the Start pill stays live and submits the drafted title.
  const inlineTitleAwaitingSubmit = Boolean(
    ownsInlineTitle && planningStart.pending?.needsTitle && !planningStart.confirmation?.submitting,
  );
  const planAllSubmitting = Boolean(planningStart.pending) && !showTitleModal && !inlineTitleAwaitingSubmit;
  const autoStartCountdownVisible = planningStart.autoStart.secondsLeft !== null
    && !planningStart.autoStart.stopped && !planningStart.autoStart.due;
  const draftableRunButtons = suggestion?.buttons.filter((button) => button.action === 'trigger_agents') ?? [];
  const draftNeeded = Boolean(
    suggestion && !isHistoricalSuggestion && !titleConfirmedAt && titleRequestKey
    && draftableRunButtons.length > 0
    && !suggestion.buttons.some((button) => isActionClicked(suggestion.messageId, button.key)),
  );
  const { registerTitleDraft } = planningStart;

  useEffect(() => {
    if (!draftNeeded || !suggestion || !titleRequestKey) return undefined;
    registerTitleDraft({
      actionKey: titleRequestKey, projectId: suggestion.projectId, sessionId: suggestion.sessionId,
      entryId: suggestion.messageId,
    });

    return () => registerTitleDraft(null, titleRequestKey);
  }, [draftNeeded, registerTitleDraft, suggestion, titleRequestKey]);
  const [submittingActionKey, setSubmittingActionKey] = useState<string | null>(null);
  const planningStatusQuery = useGetPlanningStatusQuery(
    suggestion?.projectId ?? '',
    { skip: !suggestion?.projectId },
  );

  // Subscribe to the action-bar-clicks store so persistent click state
  // re-renders this component when clicks are recorded or loaded.
  useActionBarClicksStore();
  // A balance that cannot start any offered run leads with the credit actions (18 Oct 2026: a reply explaining
  // 0.09 credits came with an enabled primary Run Design). Live, never stored: the run stays pressable.
  const offeredRuns = isHistoricalSuggestion ? [] : (suggestion?.buttons ?? [])
    .filter((button) => button.action === 'trigger_agents' && buttonPayloadAgents(button).length > 0);
  const quotedRunActions = offeredRuns.map((button) => ({
    key: button.key, agents: buttonPayloadAgents(button), label: button.label,
    ...(taskSelection.selection ? { selection: taskSelection.selection } : {}),
    ...(taskSelection.preparedInvocationId ? { invocation_id: taskSelection.preparedInvocationId } : {}),
  }));
  // Once a run is pressed or running, the bar belongs to it: stop asking what it would cost.
  const runPressedOrActive = Boolean(planningStart.pending || creditRefusal || planningStatusQuery.data?.is_active
    || offeredRuns.some((button) => suggestion && isActionClicked(suggestion.messageId, button.key)));
  const startCredit = usePlanningStartCredit(
    suggestion?.projectId, quotedRunActions, !hidden && !runPressedOrActive, suggestion?.messageId,
  );

  useEffect(() => {
    if (
      !suggestion
      || !planningStatusMatchesTrigger(planningStatusQuery.data, suggestion.messageId)
    ) return;

    const matchingRunButtons = suggestion.buttons.filter((button) => button.action === 'trigger_agents');

    if (matchingRunButtons.length !== 1) return;
    void recordActionClick(
      suggestion.messageId,
      'agent_suggestion',
      matchingRunButtons[0].key,
      suggestion.projectId,
      suggestion.sessionId,
    );
  }, [planningStatusQuery.data, suggestion]);

  // The overview panel is the single owner of transcript following. These
  // controls may be projected into both a message and the sticky action bar,
  // so scrolling from each projection would move the viewport twice.
  void autoScrollIntoView;

  if (hidden || !suggestion || showTitleModal) return null;
  const entryId = suggestion.messageId;
  const planningChecklist = showPlanningTasks ? (
    <AgentSuggestionPlanningTasks projectId={suggestion.projectId} entryId={entryId}
      buttons={suggestion.buttons} current={!isHistoricalSuggestion} createdAt={suggestion.createdAt} />
  ) : null;

  if (suggestion.buttons.length === 0) return planningChecklist;
  const blockAgentRuns = designActionBlock.blocked
    || (isDesignAgentSuggestion(suggestion.agents) && !designActionBlock.loaded);
  const visibleButtons = suggestion.buttons.filter((button) => (
    !(!isHistoricalSuggestion && blockAgentRuns && button.action === 'trigger_agents')
  ));

  if (visibleButtons.length === 0) return planningChecklist;

  const keepTalkingButton = visibleButtons.find((button) => button.key === 'keep_talking');
  const runButtons = visibleButtons.filter((button) => button.action === 'trigger_agents');
  const buildButtons = visibleButtons.filter((button) => button.action === 'start_build');
  const actionButtons = visibleButtons.filter((button) => button.kind === 'action');

  const runButtonClicked = runButtons.some((button) => isActionClicked(entryId, button.key));
  const runAcceptedByServer = planningStatusMatchesTrigger(planningStatusQuery.data, entryId);
  const buildClicked = buildButtons.some((button) => isActionClicked(entryId, button.key));
  const keepTalkingClicked = keepTalkingButton ? isActionClicked(entryId, keepTalkingButton.key) : false;
  const anyClicked = runButtonClicked || runAcceptedByServer || buildClicked || keepTalkingClicked;
  // After a pill has been clicked we keep the whole group on screen so the
  // user can see what options they had and which one they picked. The chosen
  // pill renders with the --triggered class (green ✓, neutral surface) and the rest
  // get --locked (dimmed + non-interactive) — same machinery the buttons
  // already use, just no longer hidden behind an early return.
  const runLocked = anyClicked && !runButtonClicked;
  // Once Start has been taken and its run is under way, the bar belongs to
  // that run (Cancel, plus whatever More holds): repeating a spent Start and
  // a locked Keep Talking there only reads as if nothing happened.
  const chosenRunInFlight = !isHistoricalSuggestion && (
    planAllSubmitting
    || ((runButtonClicked || runAcceptedByServer) && Boolean(planningStatusQuery.data?.is_active))
  );
  const keepTalkingLocked = anyClicked && !keepTalkingClicked;
  // Gone once a run is chosen or already running, and while a press-time refusal says the same thing in full.
  const creditShort = !anyClicked && !chosenRunInFlight && !planAllSubmitting && !creditRefusal
    && !planningStatusQuery.data?.is_active ? startCredit : null;
  const keepTalkingHistoricalLocked = Boolean(
    isHistoricalSuggestion
    && keepTalkingButton
    && !isRestorableSupervisorButton(keepTalkingButton),
  );
  const keepTalkingDisabled = keepTalkingLocked || keepTalkingHistoricalLocked;

  const anchorHere = () => {
    if (itemId) pinSuggestionAnchor(itemId, entryId, suggestion.projectId);
  };

  const runStart = async (button: SupervisorButton, invocationId?: string) => {
    anchorHere();
    const agentsToRun = buttonPayloadAgents(button);

    try {
      const result = await triggerAgentsOnServer(agentsToRun, suggestion, invocationId);
      if (result.status === 'triggered') {
        if (!invocationId && result.agents.length > 0 && result.agents.join('|') !== agentsToRun.join('|')) {
          replaceLatestSuggestionAgents(suggestion, result.agents);
        }
        if (result.reason) toast.info(result.reason);
        await recordActionClick(
          entryId,
          'agent_suggestion',
          button.key,
          suggestion.projectId,
          suggestion.sessionId,
        );
      } else {
        throw new Error(result.reason || 'No agent run was started.');
      }
    } catch (error) {
      // Out of credits: said in chat with the actions that let the run go, not in a passing toast.
      throwIfPlanningCreditRefused(error, setCreditRefusal);
      const accepted = await reconcileAmbiguousPlanningTrigger(
        error,
        suggestion.messageId,
        () => planningStatusQuery.refetch().unwrap(),
      );

      if (accepted) {
        await recordActionClick(
          entryId,
          'agent_suggestion',
          button.key,
          suggestion.projectId,
          suggestion.sessionId,
        );
        toast.info('Planning started. The confirmation response was delayed.');
      } else {
        throw new ApiError({ code: 'PLANNING_TRIGGER_FAILED', message: getTriggerAgentErrorMessage(error) });
      }
    }
  };

  // eslint-disable-next-line max-statements
  const handleRunClick = async (button: SupervisorButton): Promise<void> => {
    if (
      runButtonClicked
      || runLocked
      || (isHistoricalSuggestion && !isRestorableSupervisorButton(button))
      || planAllSubmitting
      || showTitleModal
      || taskSelection.disabled
      || buttonPayloadAgents(button).length === 0
    ) return;

    const needsTitle = !titleConfirmedAt;
    const inlineTitle = needsTitle && ownsInlineTitle ? planningStart.confirmation : null;

    // The inline form validates as the user types; a title that cannot be
    // saved keeps Start put instead of opening a request it cannot finish.
    if (inlineTitle && (inlineTitle.availability.validationError || !inlineTitle.canSave)) {
      planningStart.stopAutoStart();

      return;
    }
    // A restored or retried Start already holds the pending request: this
    // click only needs to save the drafted title to hand it over.
    if (inlineTitle && inlineTitleAwaitingSubmit) {
      void inlineTitle.submitTitle();

      return;
    }
    anchorHere();
    let invocationId = taskSelection.preparedInvocationId;
    const requestInvocationId = createActionInvocationId();
    const label = taskSelection.labelFor(button.label);
    planningStart.requestStart({
      actionKey: titleRequestKey!,
      buttonKey: button.key,
      invocationId: requestInvocationId,
      projectId: suggestion.projectId,
      sessionId: suggestion.sessionId,
      needsTitle,
      // Keep the visible action in chat even if the title dialog is cancelled.
      // The server trigger still uses the original suggestion's message ID.
      onInvocation: async () => {
        if (taskSelection.selection && invocationId) return;
        const recordedInvocationId = await recordUserActionClick(
          entryId, 'agent_suggestion', button.key, label,
          suggestion.projectId, suggestion.sessionId,
          {
            invocationId: requestInvocationId,
            required: true,
            ...(taskSelection.selection ? {
              planningSelection: taskSelection.selection,
              baseLabel: button.label,
            } : {}),
            onRecorded: (id) => {
              invocationId = id;
              if (taskSelection.selection) taskSelection.markPrepared(id, label);
            },
          },
        ).catch((error: unknown) => {
          // Replacing an earlier plan is priced at the press: its 402 is the same chat refusal as Start's.
          throwIfPlanningCreditRefused(error, setCreditRefusal);
          throw error;
        });

        invocationId = recordedInvocationId ?? invocationId;
        if (!invocationId) throw new Error('Could not prepare the planning action.');
      },
      onStart: () => runStart(button, invocationId),
    });
    // One click: the pending request is claimed above, so saving the drafted
    // title now hands straight into that start.
    if (inlineTitle) void inlineTitle.submitTitle();
  };

  const completeKeepTalking = () => {
    if (!keepTalkingButton) return;
    void recordActionClick(
      entryId,
      'agent_suggestion',
      keepTalkingButton.key,
      suggestion.projectId,
      suggestion.sessionId,
    );
    focusChatInput();
  };

  const handleKeepTalkingClick = () => {
    if (!keepTalkingButton || keepTalkingClicked || keepTalkingDisabled || flyGhost) return;
    anchorHere();
    void recordUserActionClick(
      entryId,
      'agent_suggestion',
      keepTalkingButton.key,
      keepTalkingButton.label,
      suggestion.projectId,
      suggestion.sessionId,
    );

    if (suggestion.projectId) {
      projectService.dismissSuggestions(suggestion.projectId).catch(() => {});
    }

    const btn = keepTalkingBtnRef.current;
    const input = document.querySelector<HTMLElement>('.text-input');

    if (!btn || !input) { completeKeepTalking(); return; }

    const btnRect = btn.getBoundingClientRect();
    // Sticky controls forward to this hidden source; complete immediately
    // when there is no visible source rectangle to animate from.
    if (btnRect.width <= 0 || btnRect.height <= 0) { completeKeepTalking(); return; }
    const inputRect = input.getBoundingClientRect();
    const cs = window.getComputedStyle(input);
    const padLeft = parseFloat(cs.paddingLeft) || 0;
    const padTop = parseFloat(cs.paddingTop) || 0;
    const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
    const borderTop = parseFloat(cs.borderTopWidth) || 0;
    const lineH = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 16) * 1.4;

    // Target the caret position (start of first line) rather than input center.
    const caretX = inputRect.left + borderLeft + padLeft + 2;
    const caretY = inputRect.top + borderTop + padTop + lineH / 2;
    const dx = caretX - (btnRect.left + btnRect.width / 2);
    const dy = caretY - (btnRect.top + btnRect.height / 2);

    setFlyGhost({
      x: btnRect.left, y: btnRect.top, w: btnRect.width, h: btnRect.height, dx, dy,
    });
  };

  const handleGhostAnimationEnd = () => {
    setFlyGhost(null);
    completeKeepTalking();
  };

  const handleActionButtonClick = async (button: SupervisorButton) => {
    if (!suggestion.projectId || submittingActionKey) return;
    anchorHere();
    setSubmittingActionKey(button.key);
    try {
      await recordUserActionClick(
        entryId,
        'agent_suggestion',
        button.key,
        button.label,
        suggestion.projectId,
        suggestion.sessionId,
      );
      if (button.action === 'download_code') {
        const download = await projectService.downloadProjectCode(suggestion.projectId);
        saveBlob(download.blob, download.filename || getCodeZipFilename(suggestion.projectId));
      } else if (button.action === 'navigate') {
        const pathTemplate = button.payload?.path_template;
        const path = button.payload?.path;
        const tab = button.payload?.tab;
        if (typeof tab === 'string' && tab) {
          window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: tab }));
          if (typeof button.payload?.view === 'string') {
            window.dispatchEvent(new globalThis.CustomEvent('switch_tasks_view', {
              detail: button.payload.view,
            }));
          }
        } else if (typeof pathTemplate === 'string') {
          window.location.assign(pathTemplate.replace('{project_id}', suggestion.projectId));
        } else if (typeof path === 'string') {
          window.location.assign(path);
        }
      } else if (isProjectUtilityAction(button.action)) {
        await projectActions.handleProjectActionButton(button);
      }
      await recordActionClick(
        entryId,
        'agent_suggestion',
        button.key,
        suggestion.projectId,
        suggestion.sessionId,
      );
    } catch (error) {
      toast.error(errorMessage(error, `We couldn’t run “${button.label}”. Refresh and try again.`));
    } finally {
      setSubmittingActionKey(null);
    }
  };

  const runButtonState = (button: SupervisorButton) => {
    const accepted = isActionClicked(entryId, button.key) || runAcceptedByServer;
    const historicalLocked = isHistoricalSuggestion && !isRestorableSupervisorButton(button);
    const anotherRunActive = Boolean(planningStatusQuery.data?.is_active) && !runAcceptedByServer;
    const locked = runLocked || historicalLocked || anotherRunActive;
    const enabled = !accepted
      && !locked
      && !planAllSubmitting
      && !showTitleModal
      && !taskSelection.disabled
      && button.enabled !== false
      && buttonPayloadAgents(button).length > 0;

    return { accepted, locked, anotherRunActive, enabled };
  };

  const renderRunButton = (button: SupervisorButton) => {
    const starting = planAllSubmitting && planningStart.pending?.actionKey === titleRequestKey
      && planningStart.pending.buttonKey === button.key;
    const { accepted, locked, anotherRunActive, enabled } = runButtonState(button);
    const label = accepted
      ? button.label
      : planAllSubmitting
        ? `⟳ Starting ${button.label}`
        : anotherRunActive
          ? 'Planning in progress'
          : taskSelection.labelFor(button.label);
    const creditNote = enabled ? creditShort?.noteFor(button.key) : undefined;
    // Start shows the server's countdown: "Start 18s" until the reader waits,
    // touches the title, or the server presses Start itself. Not while the balance cannot start it.
    const countdown = enabled && ownsInlineTitle && autoStartCountdownVisible && !creditNote
      ? planningStart.autoStart.secondsLeft
      : null;
    const shownLabel = countdown === null ? label : `${label} ${countdown}s`;

    return (
      <React.Fragment key={button.key}>
        <PlanningActionRegistration
          projectId={suggestion.projectId}
          sessionId={suggestion.sessionId}
          entryId={entryId}
          button={{ ...button, label }}
          actionIdentity={taskSelection.selection ? taskSelection.actionIdentity : undefined}
          enabled={enabled}
          current={!isHistoricalSuggestion}
          invoke={() => handleRunClick(button)}
        />
        <button
          className={`agent-suggestion-pill agent-suggestion-pill--combined agent-suggestion-pill--run${accepted || starting ? ' agent-suggestion-pill--triggered' : ''}${locked ? ' agent-suggestion-pill--locked' : ''}${creditNote ? ' ai-overview-action-btn--credit-short' : ''}`}
          onClick={() => { void handleRunClick(button); }}
          disabled={!enabled}
          type="button"
          title={creditNote ?? (countdown === null ? label : `${label}: starts on its own in ${countdown} seconds`)}
          aria-label={countdown === null ? label : `${label}, starts on its own in ${countdown} seconds`}
          aria-busy={starting || anotherRunActive}
        >
          {accepted && (
            <span className="agent-suggestion-pill__check" aria-hidden="true">✓</span>
          )}
          <span className="agent-suggestion-pill__label">{shownLabel}</span>
        </button>
      </React.Fragment>
    );
  };

  const renderActionButton = (button: SupervisorButton) => {
    const loading = submittingActionKey === button.key;
    const label = loading ? loadingLabelForSupervisorButton(button) : button.label;
    const className = [
      'agent-suggestion-pill',
      'agent-suggestion-pill--combined',
      'agent-suggestion-pill--action',
      `agent-suggestion-pill--${actionClassForSupervisorButton(button)}`,
      loading ? 'agent-suggestion-pill--loading' : '',
    ].filter(Boolean).join(' ');

    return (
      <button
        key={button.key}
        className={className}
        onClick={() => handleActionButtonClick(button)}
        disabled={loading || !suggestion.projectId}
        type="button"
        title={label}
        aria-label={label}
        aria-busy={loading}
      >
        {loading && <BrandPendulumMark animated={false} className="agent-suggestion-pill__spinner" />}
        <span className="agent-suggestion-pill__label">{label}</span>
      </button>
    );
  };

  return (
    <div className="agent-suggestion-buttons">
      {planningChecklist}
      <div className={`agent-suggestion-pills${standaloneActionBar ? ' ai-overview-action-bar' : ''}`}
        data-action-mirror-owner={standaloneActionBar ? isHistoricalSuggestion ? 'historical' : 'current' : undefined}
        data-action-source-entry-id={standaloneActionBar ? entryId : undefined}
        data-action-owner-label={standaloneActionBar ? 'Suggested actions' : undefined}>
        {creditShort && runButtons.some((button) => runButtonState(button).enabled) && (
          <PlanningStartCreditActions credit={creditShort} />
        )}
        {keepTalkingButton && !chosenRunInFlight && (
          <button
            ref={keepTalkingBtnRef}
            className={`agent-suggestion-pill agent-suggestion-pill--keep-talking${keepTalkingClicked ? ' agent-suggestion-pill--triggered' : ''}${keepTalkingDisabled ? ' agent-suggestion-pill--locked' : ''}`}
            style={flyGhost ? { visibility: 'hidden' } : undefined}
            onClick={handleKeepTalkingClick}
            disabled={keepTalkingClicked || keepTalkingDisabled || !!flyGhost}
            type="button"
          >
            {keepTalkingClicked && (
              <span className="agent-suggestion-pill__check" aria-hidden="true">✓</span>
            )}
            <span className="agent-suggestion-pill__label">{keepTalkingButton.label}</span>
          </button>
        )}
        {!chosenRunInFlight && runButtons.map(renderRunButton)}
        {ownsInlineTitle && autoStartCountdownVisible && !creditShort && (
          <button
            className="agent-suggestion-pill agent-suggestion-pill--combined agent-suggestion-pill--wait"
            data-action-placement="pinned"
            onClick={planningStart.stopAutoStart}
            type="button"
            title="Wait: keep the title form open instead of starting automatically"
            aria-label="Wait, do not start automatically"
          >
            <span className="agent-suggestion-pill__label">Wait</span>
          </button>
        )}
        {actionButtons.map(renderActionButton)}
        {buildButtons.map((button) => (
          <BuildPill
            key={button.key}
            projectId={suggestion.projectId}
            sessionId={suggestion.sessionId}
            entryId={entryId}
            onBeforeClick={anchorHere}
          />
        ))}
      </div>
      {creditRefusal && !runButtonClicked && !isHistoricalSuggestion && (
        <PlanningCreditRefusal refusal={creditRefusal} />
      )}
      {flyGhost && createPortal(
        <div
          className="agent-suggestion-pill agent-suggestion-pill--keep-talking agent-suggestion-pill--fly-ghost"
          style={{
            position: 'fixed',
            left: flyGhost.x,
            top: flyGhost.y,
            width: flyGhost.w,
            height: flyGhost.h,
            margin: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            ['--fly-dx' as string]: `${flyGhost.dx}px`,
            ['--fly-dy' as string]: `${flyGhost.dy}px`,
          }}
          onAnimationEnd={handleGhostAnimationEnd}
        >
          Keep Talking
        </div>,
        document.body,
      )}
      {projectActions.modals}
    </div>
  );
};

// Messages list
export const MessagesList = React.memo<{
  messages: ChatMessage[];
  userDisplayName: string;
  inlineButtons?: React.ReactNode;
  projectId?: string | null;
  sessionId?: string | null;
}>(({ messages, userDisplayName, inlineButtons, projectId, sessionId }) => {
  // Latest state per plan id, derived from `<!--8080-dev-plan-state:UUID:STATE-->`
  // markers across all messages — the buttons render based on this.
  const planStates = useMemo(() => collectDeveloperPlanStates(messages), [messages]);
  const suggestionStoreVersion = useSuggestionStoreVersion();
  const pendingStart = usePendingPlanningStart();
  const inlineButtonsForMessage = useMemo(() => (
    message: ChatMessage,
    fallback: React.ReactNode | undefined,
  ): React.ReactNode | undefined => {
    void suggestionStoreVersion;
    const ownsPendingTitleConfirmation = Boolean(
      pendingStart?.needsTitle && pendingStart.invocationId === message.id,
    );
    const ownedHistory = ownsPendingTitleConfirmation ? null : (
      <PlanningActionTasks projectId={message.project_id ?? projectId} entryId={message.id}
        createdAt={message.created_at} historyOnly />
    );

    if (message.author === 'user') return <>{ownedHistory}{fallback}</>;
    if (getActionGatePhase(message)) return fallback;
    const historicalSuggestion = historicalActionSuggestionFromMessage(message, projectId, sessionId);
    const historicalButtons = historicalSuggestion ? (
      <AgentSuggestionButtons
        key={`historical-actions-${message.id}`}
        itemId={message.id}
        projectId={historicalSuggestion.projectId}
        sessionId={historicalSuggestion.sessionId}
        suggestionOverride={historicalSuggestion}
        historical
        standaloneActionBar
      />
    ) : null;

    return <>{historicalButtons ?? ownedHistory}{fallback}</>;
  }, [pendingStart, projectId, sessionId, suggestionStoreVersion]);
  const elements = useMemo(
    () => buildMessageElements(
      messages,
      userDisplayName,
      inlineButtons,
      planStates,
      inlineButtonsForMessage,
    ),
    [messages, userDisplayName, inlineButtons, planStates, inlineButtonsForMessage],
  );

  return (
    <>
      {elements.slice().reverse()}
    </>
  );
});

MessagesList.displayName = 'MessagesList';
// Single typing indicator
interface SingleAgentTypingProps {
  content: string;
  isStreaming?: boolean;
}

/** Height (px) of one rendered line in the chat bubble (font 16 × 1.6 + p margins). */
const STREAM_LINE_H = 30;
/** Show streaming content in blocks of this many lines. */
const STREAM_PAD_LINES = 3;

/** Compute trailing spacer height to fill the current 3-line block. */
const computeStreamPad = (el: HTMLElement | null): number => {
  if (!el) return STREAM_LINE_H * (STREAM_PAD_LINES - 1);
  const lines = Math.max(1, Math.ceil(el.scrollHeight / STREAM_LINE_H));
  const r = lines % STREAM_PAD_LINES;

  return r === 0 ? 0 : (STREAM_PAD_LINES - r) * STREAM_LINE_H;
};

export const SingleAgentTyping: React.FC<SingleAgentTypingProps> = ({
  content,
  isStreaming = true,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [padH, setPadH] = useState(STREAM_LINE_H * (STREAM_PAD_LINES - 1));
  const webSearches = useAppSelector((state) => state.planning.streamingWebSearches);

  useLayoutEffect(() => {
    if (isStreaming && content) setPadH(computeStreamPad(bodyRef.current));
  }, [content, isStreaming]);

  return (
    <div className="planning-message">
      <PlanningMessageIdentity name="Tech Lead Agent" isStreaming={isStreaming} streamContent={content} />
      <WebSearchActivityList searches={webSearches} />
      <div ref={bodyRef} style={!content ? { minHeight: 8 } : undefined}>
        {content && <ExpandableContent content={content} isStreaming={isStreaming} isLast />}
      </div>
      {isStreaming && padH > 0 && <div style={{ height: padH, flexShrink: 0 }} />}
    </div>
  );
};

// Empty placeholder
export const EmptyPlaceholder: React.FC = () => (
  <div className="planning-placeholder">No messages yet. Start by typing below.</div>
);

interface ErrorBannerProps {
  error: string | null;
  reconnectState?: AutoReconnectState;
  onRetryNow?: () => void;
}

const ReconnectingSpinner: React.FC = () => (
  <span className="planning-error__spinner" aria-hidden="true" />
);

const renderCountingMeta = (
  state: Extract<AutoReconnectState, { phase: 'counting' }>,
  onRetryNow?: () => void,
) => (
  <>
    <span className="planning-error__countdown">
      Reconnecting in {state.secondsLeft}s (attempt {state.attempt} of {state.maxAttempts})
    </span>
    {onRetryNow && (
      <button className="planning-error__retry-btn" onClick={onRetryNow} type="button">
        Retry now
      </button>
    )}
  </>
);

const renderRetryingMeta = () => (
  <span className="planning-error__countdown">
    <ReconnectingSpinner />
    Reconnecting…
  </span>
);

const renderGaveUpMeta = (onRetryNow?: () => void) => onRetryNow ? (
  <button className="planning-error__retry-btn" onClick={onRetryNow} type="button">
    Try again
  </button>
) : null;

const renderCreditMeta = (
  setShowBilling: (view: 'credits' | 'plans') => void,
) => (
  <>
    <button className="planning-error__add-credits" onClick={() => setShowBilling('credits')} type="button">Add Credits</button>
    <button className="planning-error__add-credits planning-error__upgrade" onClick={() => setShowBilling('plans')} type="button">Upgrade</button>
  </>
);

// Error banner
export const ErrorBanner: React.FC<ErrorBannerProps> = ({ error, reconnectState, onRetryNow }) => {
  const [billingView, setBillingView] = useState<'credits' | 'plans' | null>(null);
  const phase = reconnectState?.phase ?? 'idle';

  if (phase === 'reconnected') {
    return (
      <div className="planning-error planning-error--success">
        <span>✓ Reconnected</span>
      </div>
    );
  }

  if (!error) return null;

  return (
    <div className="planning-error">
      <span>{error}</span>
      {phase === 'counting' && renderCountingMeta(reconnectState as Extract<AutoReconnectState, { phase: 'counting' }>, onRetryNow)}
      {phase === 'retrying' && renderRetryingMeta()}
      {phase === 'gaveUp' && renderGaveUpMeta(onRetryNow)}
      {phase === 'idle' && isCreditError(error) && <>
        <CreditRefillNotice />
        {renderCreditMeta(setBillingView)}
      </>}
      {billingView && <BillingModal source="planning:error-credit-actions" initialView={billingView} onClose={() => setBillingView(null)} />}
    </div>
  );
};
